import { useState, useMemo, useRef } from "react";
import { useAuth } from "@/hooks/use-auth";
import NotificationBell from "@/components/notification-bell";
import {
  useOwnerApartments,
  useOwnerSubmissions,
  useOwnerCleaningJobs,
  useOwnerStats,
  useOwnerReservations,
  useCreateOwnerReservation,
  useUpdateOwnerReservation,
  useDeleteOwnerReservation,
  useOwnerFinancialRecords,
  useOwnerFinancialSummary,
  useOwnerDeposits,
  useOwnerReconciliation,
  useApartments,
  useBuildings,
  ReservationConflictError,
} from "@/hooks/use-resources";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogDescription,
} from "@/components/ui/dialog";
import { useLocation as useWouterLocation } from "wouter";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { Skeleton } from "@/components/ui/skeleton";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { PhotoLightbox, useLightbox } from "@/components/photo-lightbox";
import { generateInvoicePdf } from "@/lib/invoice-pdf";
import ContractsWidget from "@/components/contracts-widget";
import staioIcon from "@/assets/images/staio-icon.png";
import {
  LogOut,
  Home,
  ClipboardList,
  Sparkles,
  ExternalLink,
  Copy,
  CheckCircle2,
  AlertCircle,
  AlertTriangle,
  Plus,
  Pencil,
  Trash2,
  Calendar,
  Download,
  ChevronLeft,
  ChevronRight,
  CalendarDays,
  ArrowUpRight,
  DollarSign,
  FileText,
  Printer,
  BarChart3,
  LifeBuoy,
  Send,
  MessageSquare,
  Clock,
  HelpCircle,
  Shield,
  Wrench,
  PlayCircle,
  ImageIcon,
  Upload,
  FileSpreadsheet,
  XCircle,
  RefreshCw,
  Camera,
  Link2,
  CreditCard,
} from "lucide-react";
import { KpiCard, MetricStrip } from "@/components/dashboard";
import LumiBriefing from "@/components/lumi-briefing";
import LumiOwnerBubble from "@/components/lumi-owner-bubble";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Separator } from "@/components/ui/separator";
import { Calendar as CalendarWidget } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import type { DateRange } from "react-day-picker";
import type { Apartment, Reservation, CleaningJob, Submission } from "@shared/routes";
import type { OwnerBillingProfile } from "@shared/schema";

type OwnerReportData = {
  dateRange: { startDate: string; endDate: string };
  reservations: {
    summary: { total: number; confirmed: number; checkedIn: number; checkedOut: number };
    records: Array<Reservation & { apartmentName: string }>;
  };
  cleaning: {
    summary: { total: number; completed: number };
    records: Array<CleaningJob & { apartmentName: string }>;
  };
  finances: {
    summary: {
      totalCharges: string;
      checkInFees: string;
      cleaningFees: string;
      unitSetupFees: string;
      maintenanceFees: string;
      miscFees: string;
      pendingAmount: string;
      paidAmount: string;
    };
    records: Array<{
      id: number;
      type: string;
      apartmentId: number | null;
      apartmentName: string;
      totalAmount: string;
      status: string;
      description: string | null;
      createdAt: string | Date | null;
    }>;
  };
  submissions: {
    summary: { total: number };
    records: Array<Submission & { apartmentName: string }>;
  };
};

function formatDate(d: string | Date) {
  return new Date(d).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  });
}

function toDateString(d: Date) {
  return d.toISOString().split("T")[0];
}

function isSameDay(a: Date, b: Date) {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function getStatusBadge(status: string) {
  switch (status) {
    case "confirmed":
      return (
        <Badge className="bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400 no-default-hover-elevate no-default-active-elevate">
          Confirmed
        </Badge>
      );
    case "checked_in":
      return (
        <Badge className="bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400 no-default-hover-elevate no-default-active-elevate">
          Checked In
        </Badge>
      );
    case "checked_out":
      return <Badge variant="secondary">Checked Out</Badge>;
    case "cancelled":
      return <Badge variant="destructive">Cancelled</Badge>;
    default:
      return <Badge variant="outline">{status}</Badge>;
  }
}

function getPriorityBadge(priority: string) {
  switch (priority) {
    case "urgent":
      return <Badge variant="destructive">Urgent</Badge>;
    case "normal":
      return <Badge variant="default">Normal</Badge>;
    case "low":
      return <Badge variant="secondary">Low</Badge>;
    default:
      return <Badge variant="outline">{priority}</Badge>;
  }
}

function getTimelineColor(status: string) {
  switch (status) {
    case "confirmed":
      return "bg-green-200 dark:bg-green-800/40";
    case "checked_in":
      return "bg-blue-200 dark:bg-blue-800/40";
    case "checked_out":
      return "bg-muted";
    default:
      return "bg-muted";
  }
}

function formatDateShort(d: string | Date) {
  return new Date(d).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  });
}

function formatMoney(amount: string | number | null | undefined): string {
  const num = Number(amount || 0);
  return `$${num.toFixed(2)}`;
}

function getReportPresetRange(preset: string): { start: Date; end: Date } {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  switch (preset) {
    case "this_week": {
      const dow = today.getDay();
      const start = new Date(today);
      start.setDate(today.getDate() - dow);
      const end = new Date(start);
      end.setDate(start.getDate() + 6);
      return { start, end };
    }
    case "last_week": {
      const dow = today.getDay();
      const end = new Date(today);
      end.setDate(today.getDate() - dow - 1);
      const start = new Date(end);
      start.setDate(end.getDate() - 6);
      return { start, end };
    }
    case "this_month": {
      const start = new Date(now.getFullYear(), now.getMonth(), 1);
      const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      return { start, end };
    }
    case "last_month": {
      const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const end = new Date(now.getFullYear(), now.getMonth(), 0);
      return { start, end };
    }
    case "this_year": {
      const start = new Date(now.getFullYear(), 0, 1);
      const end = new Date(now.getFullYear(), 11, 31);
      return { start, end };
    }
    default: {
      const start = new Date(now.getFullYear(), now.getMonth(), 1);
      const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      return { start, end };
    }
  }
}

const emptyForm = {
  apartmentId: "",
  guestName: "",
  guestEmail: "",
  guestPhone: "",
  checkInDate: "",
  checkOutDate: "",
  notes: "",
};

function OwnerCashFlowTab() {
  const getWeekRange = () => {
    const now = new Date();
    const dayOfWeek = now.getDay();
    const startOfWeek = new Date(now);
    startOfWeek.setDate(now.getDate() - dayOfWeek);
    startOfWeek.setHours(0, 0, 0, 0);
    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 6);
    return {
      start: startOfWeek.toISOString().split("T")[0],
      end: endOfWeek.toISOString().split("T")[0],
    };
  };
  const defaultRange = getWeekRange();
  const [reconStart, setReconStart] = useState(defaultRange.start);
  const [reconEnd, setReconEnd] = useState(defaultRange.end);
  const { data: reconciliation, isLoading } = useOwnerReconciliation(reconStart, reconEnd);

  const fmtMoney = (v: string | number | null | undefined) => {
    const n = Number(v || 0);
    return `$${n.toFixed(2)}`;
  };

  const ownerData = reconciliation?.ownerBreakdown?.[0];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h3 className="font-display text-lg font-semibold">Cash Flow Report</h3>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-1">
            <Label className="text-sm whitespace-nowrap">From</Label>
            <Input
              type="date"
              value={reconStart}
              onChange={(e) => setReconStart(e.target.value)}
              className="w-auto"
              data-testid="input-owner-recon-start"
            />
          </div>
          <div className="flex items-center gap-1">
            <Label className="text-sm whitespace-nowrap">To</Label>
            <Input
              type="date"
              value={reconEnd}
              onChange={(e) => setReconEnd(e.target.value)}
              className="w-auto"
              data-testid="input-owner-recon-end"
            />
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="text-center py-8 text-muted-foreground">Loading cash flow data...</div>
      ) : !ownerData ? (
        <Card>
          <CardContent className="p-6 text-center text-muted-foreground">
            <BarChart3 className="w-10 h-10 mx-auto mb-2 opacity-30" />
            <p>No cash flow data for this period</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          <MetricStrip cols={4}>
            <KpiCard label="Collected" value={fmtMoney(ownerData.cashCollected)} icon={DollarSign} variant="success" testId="text-owner-cash-collected" />
            <KpiCard label="Charges" value={fmtMoney(ownerData.serviceCharges?.total)} icon={CreditCard} variant="info" testId="text-owner-service-charges" />
            <KpiCard label="Settled" value={fmtMoney(ownerData.settlements)} icon={CheckCircle2} variant="default" testId="text-owner-settlements" />
            <KpiCard
              label={Number(ownerData.netOwed) > 0 ? "Owed to you" : Number(ownerData.netOwed) < 0 ? "You owe" : "Net"}
              value={fmtMoney(ownerData.netOwed)}
              icon={ArrowUpRight}
              variant={Number(ownerData.netOwed) > 0 ? "success" : Number(ownerData.netOwed) < 0 ? "danger" : "neutral"}
              testId="text-owner-net-owed"
            />
          </MetricStrip>

          <Card>
            <CardHeader>
              <CardTitle className="text-base font-display">Fee Breakdown</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <span className="text-muted-foreground">Check-in Fees: </span>
                  <span className="font-medium" data-testid="text-owner-fee-checkin">{fmtMoney(ownerData.serviceCharges?.checkIn)}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Cleaning Fees: </span>
                  <span className="font-medium" data-testid="text-owner-fee-cleaning">{fmtMoney(ownerData.serviceCharges?.cleaning)}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Maintenance: </span>
                  <span className="font-medium" data-testid="text-owner-fee-maintenance">{fmtMoney(ownerData.serviceCharges?.maintenance)}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Miscellaneous: </span>
                  <span className="font-medium" data-testid="text-owner-fee-misc">{fmtMoney(ownerData.serviceCharges?.misc)}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {(Number(ownerData.depositsCollected) > 0 || Number(ownerData.depositsHeld) > 0) && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base font-display">Security Deposits</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">Collected: </span>
                    <span className="font-medium">{fmtMoney(ownerData.depositsCollected)}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Returned: </span>
                    <span className="font-medium">{fmtMoney(ownerData.depositsReturned)}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Currently Held: </span>
                    <span className="font-medium">{fmtMoney(ownerData.depositsHeld)}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader>
              <CardTitle className="text-base font-display">Summary</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Cash collected from reservations</span>
                  <span className="font-medium">{fmtMoney(ownerData.cashCollected)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Minus service charges</span>
                  <span className="font-medium">-{fmtMoney(ownerData.serviceCharges?.total)}</span>
                </div>
                {Number(ownerData.serviceCharges?.maintenance) > 0 && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Minus maintenance costs</span>
                    <span className="font-medium">-{fmtMoney(ownerData.serviceCharges?.maintenance)}</span>
                  </div>
                )}
                {Number(ownerData.settlements) > 0 && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Minus already settled</span>
                    <span className="font-medium">-{fmtMoney(ownerData.settlements)}</span>
                  </div>
                )}
                <Separator />
                <div className="flex justify-between font-semibold text-base">
                  <span>{Number(ownerData.netOwed) >= 0 ? "Admin owes you" : "You owe admin"}</span>
                  <span data-testid="text-owner-net-final">{fmtMoney(Math.abs(Number(ownerData.netOwed)))}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}

type OwnerInvoiceResponse = {
  id: number;
  invoiceNumber: string;
  status: string;
  periodStart: string | Date;
  periodEnd: string | Date;
  ownerName: string | null;
  companySnapshot: Record<string, unknown> | null;
  adminCompanySnapshot: Record<string, unknown> | null;
  orgLogoUrl: string | null;
  lineItems: Array<{ description: string; category: string; amount?: string; total?: string; quantity?: number }> | null;
  subtotal: string | null;
  cashCollected: string | null;
  settlements: string | null;
  netOwed: string | null;
  paidAmount: string | null;
  notes: string | null;
  createdAt: string | Date | null;
  issuedAt: string | Date | null;
  payments?: Array<{ id: number; amount: string; paymentMethod: string | null; notes: string | null; createdAt: string | Date }>;
};

function OwnerInvoicesTab() {
  const { toast } = useToast();
  const { data: invoices, isLoading } = useQuery<OwnerInvoiceResponse[]>({
    queryKey: ["/api/owner/invoices"],
  });

  const { data: billingProfile } = useQuery<OwnerBillingProfile | null>({
    queryKey: ["/api/owner/billing-profile"],
  });

  const { data: orgLogoData } = useQuery<{ dataUrl: string | null }>({
    queryKey: ["/api/owner/org-logo-data"],
  });

  if (isLoading) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
      </div>
    );
  }

  if (!invoices || invoices.length === 0) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <p className="text-muted-foreground" data-testid="text-no-invoices">No invoices available yet</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {billingProfile && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Your Billing Profile</CardTitle>
          </CardHeader>
          <CardContent className="text-sm space-y-1">
            <p className="font-medium">{billingProfile.companyName}</p>
            {billingProfile.taxId && <p className="text-muted-foreground">Tax ID: {billingProfile.taxId}</p>}
            {billingProfile.addressLine1 && <p className="text-muted-foreground">{billingProfile.addressLine1}</p>}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Invoice #</TableHead>
                <TableHead>Period</TableHead>
                <TableHead className="text-right">Service Charges</TableHead>
                <TableHead className="text-right">Net Owed</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {invoices.map((inv: any) => (
                <TableRow key={inv.id} data-testid={`row-invoice-${inv.id}`}>
                  <TableCell className="font-mono text-sm">{inv.invoiceNumber}</TableCell>
                  <TableCell className="text-sm">
                    {formatDate(inv.periodStart)} - {formatDate(inv.periodEnd)}
                  </TableCell>
                  <TableCell className="text-right">{formatMoney(inv.subtotal)}</TableCell>
                  <TableCell className="text-right font-medium">{formatMoney(inv.netOwed)}</TableCell>
                  <TableCell>
                    <Badge variant={["issued", "approved", "partially_paid", "paid"].includes(inv.status) ? "default" : "secondary"}>
                      {inv.status === "partially_paid" ? "Partially Paid" : inv.status.charAt(0).toUpperCase() + inv.status.slice(1)}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Dialog>
                      <DialogTrigger asChild>
                        <Button size="sm" variant="ghost" data-testid={`button-view-invoice-${inv.id}`}>
                          <FileText className="h-4 w-4" />
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="max-w-2xl">
                        <DialogHeader>
                          <DialogTitle>Invoice {inv.invoiceNumber}</DialogTitle>
                          <DialogDescription>Service charges for period</DialogDescription>
                        </DialogHeader>
                        <div className="space-y-4">
                          {inv.companySnapshot && (
                            <div className="text-sm space-y-1">
                              <p className="font-medium">{inv.companySnapshot.companyName}</p>
                              {inv.companySnapshot.taxId && <p>Tax ID: {inv.companySnapshot.taxId}</p>}
                              {inv.companySnapshot.addressLine1 && <p>{inv.companySnapshot.addressLine1}</p>}
                              {(inv.companySnapshot.city || inv.companySnapshot.state) && (
                                <p>{[inv.companySnapshot.city, inv.companySnapshot.state, inv.companySnapshot.postalCode].filter(Boolean).join(", ")}</p>
                              )}
                            </div>
                          )}
                          <div className="text-sm">
                            <span className="text-muted-foreground">Period: </span>
                            {formatDate(inv.periodStart)} - {formatDate(inv.periodEnd)}
                          </div>
                          <div className="overflow-x-auto">
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead>Description</TableHead>
                                <TableHead>Date</TableHead>
                                <TableHead className="text-right">Amount</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {(inv.lineItems || []).map((item: any, i: number) => (
                                <TableRow key={i}>
                                  <TableCell>{item.description}</TableCell>
                                  <TableCell>{item.date ? formatDateShort(item.date) : '-'}</TableCell>
                                  <TableCell className="text-right">{formatMoney(item.amount)}</TableCell>
                                </TableRow>
                              ))}
                              <TableRow>
                                <TableCell className="font-medium" colSpan={2}>Total Service Charges</TableCell>
                                <TableCell className="text-right font-medium">{formatMoney(inv.subtotal)}</TableCell>
                              </TableRow>
                            </TableBody>
                          </Table>
                          </div>
                          <div className="space-y-2 pt-2">
                            <div className="flex justify-between text-sm">
                              <span className="text-muted-foreground">Cash Collected</span>
                              <span>{formatMoney(inv.cashCollected)}</span>
                            </div>
                            <div className="flex justify-between text-sm">
                              <span className="text-muted-foreground">Service Charges</span>
                              <span>-{formatMoney(inv.subtotal)}</span>
                            </div>
                            <div className="flex justify-between text-sm">
                              <span className="text-muted-foreground">Settlements</span>
                              <span>-{formatMoney(inv.settlements)}</span>
                            </div>
                            <div className="border-t pt-2 flex justify-between font-medium">
                              <span>Net Owed to You</span>
                              <span>{formatMoney(inv.netOwed)}</span>
                            </div>
                            {Number(inv.paidAmount || 0) > 0 && (
                              <>
                                <div className="flex justify-between text-sm">
                                  <span className="text-muted-foreground">Amount Paid</span>
                                  <span className="text-green-600 dark:text-green-400 font-medium">{formatMoney(inv.paidAmount)}</span>
                                </div>
                                <div className="flex justify-between text-sm font-medium">
                                  <span>Remaining Balance</span>
                                  <span>{formatMoney(Math.abs(Number(inv.netOwed || 0)) - Number(inv.paidAmount || 0))}</span>
                                </div>
                                <div className="w-full bg-muted rounded-full h-2">
                                  <div
                                    className="bg-green-500 h-2 rounded-full"
                                    style={{
                                      width: `${Math.min(100, (Number(inv.paidAmount || 0) / Math.max(0.01, Math.abs(Number(inv.netOwed || 0)))) * 100)}%`,
                                    }}
                                  />
                                </div>
                              </>
                            )}
                          </div>
                          {inv.payments && inv.payments.length > 0 && (
                            <div>
                              <p className="text-sm font-medium mb-2">Payment History</p>
                              <div className="space-y-2">
                                {inv.payments.map((p: NonNullable<OwnerInvoiceResponse['payments']>[0]) => (
                                  <div key={p.id} className="flex justify-between items-start text-sm border rounded p-2">
                                    <div>
                                      <span className="font-medium">{formatMoney(p.amount)}</span>
                                      <span className="text-muted-foreground ml-2 capitalize">{(p.paymentMethod || "").replace("_", " ")}</span>
                                      {p.notes && <p className="text-xs text-muted-foreground mt-0.5">{p.notes}</p>}
                                    </div>
                                    <span className="text-muted-foreground text-xs whitespace-nowrap">{formatDate(p.createdAt)}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          {inv.notes && (
                            <div className="text-sm">
                              <span className="text-muted-foreground">Notes: </span>{inv.notes}
                            </div>
                          )}
                          {["issued", "sent", "partially_paid"].includes(inv.status) && inv.publicToken && (
                            <div className="rounded-md border border-green-200 bg-green-50 p-4">
                              <p className="text-sm font-medium flex items-center gap-1.5 mb-3 text-green-800">
                                <CreditCard className="h-4 w-4" />
                                Pay this invoice online
                              </p>
                              <a
                                href={`/pay/${inv.publicToken}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-2 rounded-md bg-green-600 hover:bg-green-700 text-white text-sm font-semibold px-4 py-2 transition-colors"
                              >
                                <Link2 className="h-4 w-4" />
                                Pay online →
                              </a>
                            </div>
                          )}
                          <div className="pt-2">
                            <Button
                              variant="outline"
                              size="sm"
                              data-testid={`button-download-invoice-${inv.id}`}
                              onClick={() => {
                                try {
                                  generateInvoicePdf({
                                    invoiceNumber: inv.invoiceNumber,
                                    status: inv.status,
                                    periodStart: inv.periodStart,
                                    periodEnd: inv.periodEnd,
                                    ownerName: inv.ownerName || 'Property Manager',
                                    companySnapshot: inv.companySnapshot,
                                    adminCompanySnapshot: inv.adminCompanySnapshot,
                                    orgLogoUrl: orgLogoData?.dataUrl ?? null,
                                    lineItems: inv.lineItems || [],
                                    subtotal: inv.subtotal,
                                    cashCollected: inv.cashCollected,
                                    settlements: inv.settlements,
                                    netOwed: inv.netOwed,
                                    notes: inv.notes,
                                    createdAt: inv.createdAt,
                                    issuedAt: inv.issuedAt,
                                    payLinkUrl: inv.publicToken ? `${window.location.origin}/pay/${inv.publicToken}` : undefined,
                                  });
                                } catch {
                                  toast({ title: "Error", description: "Failed to generate PDF.", variant: "destructive" });
                                }
                              }}
                            >
                              <Download className="h-4 w-4 mr-2" />
                              Download PDF
                            </Button>
                          </div>
                        </div>
                      </DialogContent>
                    </Dialog>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}

export default function OwnerDashboard() {
  const { user, logout } = useAuth();
  const [, navigateToHelp] = useWouterLocation();

  const { data: orgFeatures } = useQuery<{ featureKey: string; enabled: boolean }[]>({
    queryKey: ["/api/admin/features"],
  });
  const showLumi = useMemo(() => {
    const userDisabled = new Set<string>(Array.isArray((user as any)?.disabledFeatures) ? (user as any).disabledFeatures : []);
    if (userDisabled.has("lumi_ai")) return false;
    if (!orgFeatures) return true;
    const orgEnabled = orgFeatures.find(f => f.featureKey === "lumi_ai");
    return !orgEnabled || orgEnabled.enabled;
  }, [orgFeatures, user]);

  const { data: apartments = [], isLoading: loadingApts } = useOwnerApartments();
  const { data: submissions = [] } = useOwnerSubmissions();
  const { data: cleaningJobs = [] } = useOwnerCleaningJobs();
  const { data: maintenanceJobs = [], isLoading: loadingMaint } = useQuery<any[]>({
    queryKey: ["/api/owner/maintenance-jobs"],
  });
  const { data: repairEstimates = [], isLoading: loadingEstimates } = useQuery<any[]>({
    queryKey: ["/api/my/maintenance-estimates"],
  });
  const { data: stats } = useOwnerStats();
  const { data: reservations = [], isLoading: loadingRes } = useOwnerReservations();
  const { data: financialRecords = [], isLoading: loadingFinances } = useOwnerFinancialRecords();
  const { data: financialSummary } = useOwnerFinancialSummary();
  const { data: ownerDeposits = [], isLoading: loadingDeposits } = useOwnerDeposits();
  const { data: allApartments = [] } = useApartments();
  const { data: buildings = [] } = useBuildings();
  const createReservation = useCreateOwnerReservation();
  const updateReservation = useUpdateOwnerReservation();
  const deleteReservation = useDeleteOwnerReservation();
  const { toast } = useToast();

  const { lightboxState: maintLightbox, openLightbox: openMaintLightbox, closeLightbox: closeMaintLightbox } = useLightbox();
  const [activeTab, setActiveTab] = useState("dashboard");
  const [createOpen, setCreateOpen] = useState(false);
  const [createAptOpen, setCreateAptOpen] = useState(false);
  const [aptForm, setAptForm] = useState({ name: "", buildingId: "", bedrooms: "1", maxGuests: "2", checkoutTime: "11:00 AM" });
  const [editOpen, setEditOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [timelineOffset, setTimelineOffset] = useState(0);
  const [overlapConflict, setOverlapConflict] = useState<{ overlaps: any[] } | null>(null);

  const [importOpen, setImportOpen] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [importResults, setImportResults] = useState<{ created: number; updated: number; skipped: number; total: number; results: any[] } | null>(null);
  const [importPreview, setImportPreview] = useState<{ totalRows: number; matched: number; unmatched: string[]; apartments: { id: number; name: string }[] } | null>(null);
  const [importNameMapping, setImportNameMapping] = useState<Record<string, number>>({});
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importStep, setImportStep] = useState<'upload' | 'mapping' | 'results'>('upload');
  const csvFileInputRef = useRef<HTMLInputElement>(null);

  const queryClientRef = useQueryClient();
  const [ticketCreateOpen, setTicketCreateOpen] = useState(false);
  const [ticketDetailOpen, setTicketDetailOpen] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState<any>(null);
  const [ticketForm, setTicketForm] = useState({ subject: "", category: "general", body: "", apartmentId: "none" });
  const [ticketReplyText, setTicketReplyText] = useState("");
  const [cleaningStatusFilter, setCleaningStatusFilter] = useState<string>("all");
  const [cleaningSortOrder, setCleaningSortOrder] = useState<string>("newest");
  const [cleaningAptFilter, setCleaningAptFilter] = useState<string>("all");


  const { data: ownerTickets = [], isLoading: loadingTickets } = useQuery<any[]>({
    queryKey: ["/api/owner/tickets"],
    enabled: activeTab === "tickets",
  });

  const { data: ticketMessages = [], isLoading: messagesLoading } = useQuery<any[]>({
    queryKey: ["/api/admin/tickets", selectedTicket?.id, "messages"],
    queryFn: async () => {
      const res = await fetch(`/api/admin/tickets/${selectedTicket.id}/messages`);
      if (!res.ok) throw new Error("Failed to fetch messages");
      return res.json();
    },
    enabled: !!selectedTicket?.id && ticketDetailOpen,
  });

  const createTicketMutation = useMutation({
    mutationFn: async (data: { subject: string; category: string; body: string; apartmentId?: number }) => {
      const res = await apiRequest("POST", "/api/owner/tickets", data);
      return res.json();
    },
    onSuccess: () => {
      queryClientRef.invalidateQueries({ queryKey: ["/api/owner/tickets"] });
      setTicketCreateOpen(false);
      setTicketForm({ subject: "", category: "general", body: "", apartmentId: "none" });
      toast({ title: "Ticket created" });
    },
    onError: (err: Error) => {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    },
  });

  const ticketReplyMutation = useMutation({
    mutationFn: async ({ id, body }: { id: number; body: string }) => {
      const res = await apiRequest("POST", `/api/admin/tickets/${id}/messages`, { body });
      return res.json();
    },
    onSuccess: () => {
      queryClientRef.invalidateQueries({ queryKey: ["/api/admin/tickets", selectedTicket?.id, "messages"] });
      setTicketReplyText("");
      toast({ title: "Reply sent" });
    },
    onError: (err: Error) => {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    },
  });

  const createAptMutation = useMutation({
    mutationFn: async (data: { name: string; buildingId: number; bedrooms: number; maxGuests: number; checkoutTime: string }) => {
      const res = await apiRequest("POST", "/api/owner/apartments", data);
      return res.json();
    },
    onSuccess: () => {
      queryClientRef.invalidateQueries({ queryKey: ["/api/owner/apartments"] });
      queryClientRef.invalidateQueries({ queryKey: ["/api/owner/stats"] });
      queryClientRef.invalidateQueries({ queryKey: ["/api/apartments"] });
      setCreateAptOpen(false);
      setAptForm({ name: "", buildingId: "", bedrooms: "1", maxGuests: "2", checkoutTime: "11:00 AM" });
      toast({ title: "Property created successfully" });
    },
    onError: (err: Error) => {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    },
  });

  const ticketStatusConfig: Record<string, { label: string; variant: "default" | "secondary" | "destructive" | "outline" }> = {
    open: { label: "Open", variant: "destructive" },
    in_progress: { label: "In Progress", variant: "secondary" },
    resolved: { label: "Resolved", variant: "default" },
    closed: { label: "Closed", variant: "outline" },
  };

  const ticketPriorityConfig: Record<string, { label: string; variant: "default" | "secondary" | "destructive" | "outline" }> = {
    low: { label: "Low", variant: "outline" },
    medium: { label: "Medium", variant: "secondary" },
    high: { label: "High", variant: "destructive" },
    urgent: { label: "Urgent", variant: "destructive" },
  };

  const ticketCategoryLabels: Record<string, string> = {
    access: "Access",
    maintenance: "Maintenance",
    cleaning_issue: "Cleaning Issue",
    billing: "Billing",
    general: "General",
  };

  const [reportPreset, setReportPreset] = useState("this_month");
  const [reportDateRange, setReportDateRange] = useState<DateRange | undefined>(() => {
    const { start, end } = getReportPresetRange("this_month");
    return { from: start, to: end };
  });
  const [reportAptFilter, setReportAptFilter] = useState("all");
  const [reportCalendarOpen, setReportCalendarOpen] = useState(false);

  const reportStartStr = reportDateRange?.from?.toISOString().split("T")[0] || "";
  const reportEndStr = reportDateRange?.to?.toISOString().split("T")[0] || "";

  const reportUrl = useMemo(() => {
    const params = new URLSearchParams();
    if (reportStartStr) params.set("startDate", reportStartStr);
    if (reportEndStr) params.set("endDate", reportEndStr);
    if (reportAptFilter !== "all") params.set("apartmentId", reportAptFilter);
    return `/api/owner/reports?${params.toString()}`;
  }, [reportStartStr, reportEndStr, reportAptFilter]);

  const { data: reportData, isLoading: loadingReport } = useQuery<OwnerReportData>({
    queryKey: [reportUrl],
    enabled: !!reportStartStr && !!reportEndStr && activeTab === "reports",
  });

  function handleReportPreset(value: string) {
    setReportPreset(value);
    if (value !== "custom") {
      const { start, end } = getReportPresetRange(value);
      setReportDateRange({ from: start, to: end });
    }
  }

  const handleCSVPreview = async (file: File) => {
    setImportLoading(true);
    setImportPreview(null);
    setImportResults(null);
    setImportFile(file);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetch('/api/owner/reservations/csv-preview', {
        method: 'POST',
        body: formData,
        credentials: 'include',
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.message || 'Preview failed');
      }
      const data = await res.json();
      setImportPreview(data);
      if (data.unmatched.length > 0) {
        setImportStep('mapping');
        setImportNameMapping({});
      } else {
        await executeImport(file, {});
      }
    } catch (err: any) {
      toast({ title: "Preview Error", description: err.message, variant: "destructive" });
    } finally {
      setImportLoading(false);
    }
  };

  const executeImport = async (file: File, mapping: Record<string, number>) => {
    setImportLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      if (Object.keys(mapping).length > 0) {
        formData.append('nameMapping', JSON.stringify(mapping));
      }
      const res = await fetch('/api/owner/reservations/csv-import', {
        method: 'POST',
        body: formData,
        credentials: 'include',
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.message || 'Import failed');
      }
      const data = await res.json();
      setImportResults(data);
      setImportStep('results');
      if (data.created > 0 || data.updated > 0) {
        queryClientRef.invalidateQueries({ queryKey: ['/api/owner/reservations'] });
        const parts = [];
        if (data.created > 0) parts.push(`${data.created} created`);
        if (data.updated > 0) parts.push(`${data.updated} updated`);
        toast({ title: `Import complete: ${parts.join(', ')}` });
      }
    } catch (err: any) {
      toast({ title: "Import Error", description: err.message, variant: "destructive" });
    } finally {
      setImportLoading(false);
      if (csvFileInputRef.current) csvFileInputRef.current.value = '';
    }
  };

  const handleImportWithMapping = async () => {
    if (!importFile) return;
    await executeImport(importFile, importNameMapping);
  };

  const typedApartments = apartments as Apartment[];
  const typedReservations = reservations as Reservation[];
  const typedCleaningJobs = cleaningJobs as (CleaningJob & { apartmentName?: string; assignedToName?: string; secondCleanerName?: string })[];
  const typedSubmissions = submissions as Submission[];

  const filteredCleaningJobs = useMemo(() => {
    let jobs = [...typedCleaningJobs];
    if (cleaningStatusFilter !== "all") {
      jobs = jobs.filter(j => j.status === cleaningStatusFilter);
    }
    if (cleaningAptFilter !== "all") {
      jobs = jobs.filter(j => String(j.apartmentId) === cleaningAptFilter);
    }
    jobs.sort((a, b) => {
      if (cleaningSortOrder === "newest") {
        return new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime();
      }
      if (cleaningSortOrder === "oldest") {
        return new Date(a.createdAt || 0).getTime() - new Date(b.createdAt || 0).getTime();
      }
      if (cleaningSortOrder === "scheduled") {
        return new Date(a.scheduledDate || 0).getTime() - new Date(b.scheduledDate || 0).getTime();
      }
      return 0;
    });
    return jobs;
  }, [typedCleaningJobs, cleaningStatusFilter, cleaningAptFilter, cleaningSortOrder]);

  const cleaningCounts = useMemo(() => ({
    all: typedCleaningJobs.length,
    pending: typedCleaningJobs.filter(j => j.status === "pending").length,
    in_progress: typedCleaningJobs.filter(j => j.status === "in_progress").length,
    completed: typedCleaningJobs.filter(j => j.status === "completed").length,
  }), [typedCleaningJobs]);

  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  const timelineDays = useMemo(() => {
    const days: Date[] = [];
    for (let i = 0; i < 14; i++) {
      const d = new Date(today);
      d.setDate(d.getDate() + i + timelineOffset);
      days.push(d);
    }
    return days;
  }, [today, timelineOffset]);

  const timelineApartments = useMemo(() => {
    const aptIds = new Set(typedReservations.map((r) => r.apartmentId));
    return typedApartments.filter((a) => aptIds.has(a.id));
  }, [typedReservations, typedApartments]);

  const getReservationsForCell = (apartmentId: number, day: Date) => {
    return typedReservations.filter((r) => {
      if (r.apartmentId !== apartmentId) return false;
      if (r.status === "cancelled") return false;
      const checkIn = new Date(r.checkInDate);
      const checkOut = new Date(r.checkOutDate);
      checkIn.setHours(0, 0, 0, 0);
      checkOut.setHours(0, 0, 0, 0);
      return day >= checkIn && day < checkOut;
    });
  };

  const getApartmentName = (apartmentId: number | null | undefined) => {
    if (apartmentId == null) return "Unknown Apartment";
    const apt = typedApartments.find((a) => a.id === apartmentId);
    return apt?.name || `Apartment #${apartmentId}`;
  };

  const isMyReservation = (r: Reservation) => {
    return !r.ownerUserId || r.ownerUserId === user?.id;
  };

  const sortedReservations = useMemo(() => {
    return [...typedReservations].sort(
      (a, b) => new Date(a.checkInDate).getTime() - new Date(b.checkInDate).getTime()
    );
  }, [typedReservations]);

  const copyGuestLink = (apt: Apartment) => {
    const url = `${window.location.origin}/g/${apt.slug}?t=${apt.publicToken}`;
    navigator.clipboard.writeText(url);
    toast({ title: "Link copied!", description: `Guest link for ${apt.name} copied to clipboard.` });
  };

  const handleCreate = async () => {
    if (!form.apartmentId || !form.guestName || !form.checkInDate || !form.checkOutDate) {
      toast({ title: "Error", description: "Please fill in all required fields", variant: "destructive" });
      return;
    }
    try {
      await createReservation.mutateAsync({
        apartmentId: Number(form.apartmentId),
        guestName: form.guestName,
        guestEmail: form.guestEmail || undefined,
        guestPhone: form.guestPhone || undefined,
        checkInDate: form.checkInDate,
        checkOutDate: form.checkOutDate,
        status: "confirmed",
        notes: form.notes || undefined,
        source: "manual",
      });
      setForm({ ...emptyForm });
      setCreateOpen(false);
      toast({ title: "Reservation created" });
    } catch (err: any) {
      if (err instanceof ReservationConflictError) {
        setOverlapConflict({ overlaps: err.overlaps });
      } else {
        toast({ title: "Error", description: err.message, variant: "destructive" });
      }
    }
  };

  const handleEdit = (r: Reservation) => {
    setEditingId(r.id);
    setForm({
      apartmentId: String(r.apartmentId),
      guestName: r.guestName,
      guestEmail: r.guestEmail || "",
      guestPhone: r.guestPhone || "",
      checkInDate: r.checkInDate ? toDateString(new Date(r.checkInDate)) : "",
      checkOutDate: r.checkOutDate ? toDateString(new Date(r.checkOutDate)) : "",
      notes: r.notes || "",
    });
    setEditOpen(true);
  };

  const handleUpdate = async () => {
    if (!editingId) return;
    try {
      await updateReservation.mutateAsync({
        id: editingId,
        data: {
          guestName: form.guestName || undefined,
          guestEmail: form.guestEmail || undefined,
          guestPhone: form.guestPhone || undefined,
          checkInDate: form.checkInDate || undefined,
          checkOutDate: form.checkOutDate || undefined,
          notes: form.notes || undefined,
        },
      });
      setEditOpen(false);
      setEditingId(null);
      setForm({ ...emptyForm });
      toast({ title: "Reservation updated" });
    } catch (err: any) {
      if (err instanceof ReservationConflictError) {
        setOverlapConflict({ overlaps: err.overlaps });
      } else {
        toast({ title: "Error", description: err.message, variant: "destructive" });
      }
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await deleteReservation.mutateAsync(id);
      toast({ title: "Reservation deleted" });
    } catch (err: any) {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    }
  };

  const cleaningStatusConfig: Record<string, { label: string; className: string }> = {
    pending: { label: "Pending", className: "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400 no-default-hover-elevate no-default-active-elevate" },
    in_progress: { label: "In Progress", className: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400 no-default-hover-elevate no-default-active-elevate" },
    completed: { label: "Completed", className: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400 no-default-hover-elevate no-default-active-elevate" },
  };

  const ReservationFormFields = () => (
    <div className="space-y-4">
      <div>
        <Label>Apartment</Label>
        <Select value={form.apartmentId} onValueChange={(v) => setForm((p) => ({ ...p, apartmentId: v }))}>
          <SelectTrigger data-testid="select-reservation-apartment">
            <SelectValue placeholder="Select apartment" />
          </SelectTrigger>
          <SelectContent>
            {typedApartments.map((a) => (
              <SelectItem key={a.id} value={String(a.id)}>
                {a.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label>Guest Name</Label>
        <Input
          value={form.guestName}
          onChange={(e) => setForm((p) => ({ ...p, guestName: e.target.value }))}
          placeholder="John Smith"
          data-testid="input-guest-name"
        />
      </div>
      <div>
        <Label>Guest Email</Label>
        <Input
          type="email"
          value={form.guestEmail}
          onChange={(e) => setForm((p) => ({ ...p, guestEmail: e.target.value }))}
          placeholder="guest@example.com"
          data-testid="input-guest-email"
        />
      </div>
      <div>
        <Label>Guest Phone</Label>
        <Input
          value={form.guestPhone}
          onChange={(e) => setForm((p) => ({ ...p, guestPhone: e.target.value }))}
          placeholder="+1 555-0100"
          data-testid="input-guest-phone"
        />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <Label>Check-in Date</Label>
          <Input
            type="date"
            value={form.checkInDate}
            onChange={(e) => setForm((p) => ({ ...p, checkInDate: e.target.value }))}
            data-testid="input-check-in"
          />
        </div>
        <div>
          <Label>Check-out Date</Label>
          <Input
            type="date"
            value={form.checkOutDate}
            onChange={(e) => setForm((p) => ({ ...p, checkOutDate: e.target.value }))}
            data-testid="input-check-out"
          />
        </div>
      </div>
      <div>
        <Label>Notes</Label>
        <Textarea
          value={form.notes}
          onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))}
          placeholder="Special requests, late arrival, etc."
          data-testid="input-notes"
          className="resize-none"
        />
      </div>
    </div>
  );

  if (loadingApts) {
    return (
      <div className="min-h-screen bg-muted/20 flex items-center justify-center">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-[100dvh]" style={{ background: "linear-gradient(135deg, hsl(30 33% 98%) 0%, hsl(30 20% 96%) 100%)" }}>
      <header className="bg-white/80 dark:bg-card/80 backdrop-blur-md border-b px-4 flex items-center justify-between gap-2 sticky top-0 z-50" style={{ height: 'calc(3.5rem + env(safe-area-inset-top, 0px))', paddingTop: 'env(safe-area-inset-top, 0px)' }}>
        <div className="flex items-center gap-2">
          <img src={staioIcon} alt="Staio" className="w-8 h-8 rounded-lg flex-shrink-0" />
          <div className="min-w-0">
            <h1 className="font-display font-bold text-lg leading-tight truncate" data-testid="text-owner-title">Property Manager Portal</h1>
            <p className="text-xs text-muted-foreground truncate">{(user as any)?.fullName || (user as any)?.username}</p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <NotificationBell />
          <Button variant="ghost" size="icon" onClick={() => navigateToHelp("/help")} data-testid="button-owner-help">
            <HelpCircle className="h-5 w-5" />
          </Button>
          <Button variant="ghost" size="icon" onClick={() => logout()} data-testid="button-owner-logout">
            <LogOut className="h-5 w-5" />
          </Button>
        </div>
      </header>

      <div className="p-4 space-y-6 max-w-4xl mx-auto" style={{ paddingBottom: 'calc(5rem + env(safe-area-inset-bottom, 0px))' }}>
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <div className="overflow-x-auto -mx-4 px-4 no-scrollbar" data-testid="tabs-navigation">
            <TabsList className="inline-flex w-auto min-w-full sm:w-full h-10 gap-0.5 p-1">
              <TabsTrigger value="dashboard" className="whitespace-nowrap text-xs px-3 gap-1" data-testid="tab-dashboard">
                <BarChart3 className="w-3.5 h-3.5 shrink-0" />
                Dashboard
              </TabsTrigger>
              <TabsTrigger value="properties" className="whitespace-nowrap text-xs px-3 gap-1" data-testid="tab-properties">
                <Home className="w-3.5 h-3.5 shrink-0" />
                Properties
              </TabsTrigger>
              <TabsTrigger value="reservations" className="whitespace-nowrap text-xs px-3 gap-1" data-testid="tab-reservations">
                <CalendarDays className="w-3.5 h-3.5 shrink-0" />
                Bookings
              </TabsTrigger>
              <TabsTrigger value="cleaning" className="whitespace-nowrap text-xs px-3 gap-1" data-testid="tab-cleaning">
                <Sparkles className="w-3.5 h-3.5 shrink-0" />
                Cleaning
              </TabsTrigger>
              <TabsTrigger value="maintenance" className="whitespace-nowrap text-xs px-3 gap-1" data-testid="tab-maintenance">
                <Wrench className="w-3.5 h-3.5 shrink-0" />
                Maintenance
              </TabsTrigger>
              <TabsTrigger value="finances" className="whitespace-nowrap text-xs px-3 gap-1" data-testid="tab-finances">
                <DollarSign className="w-3.5 h-3.5 shrink-0" />
                Finances
              </TabsTrigger>
              <TabsTrigger value="cashflow" className="whitespace-nowrap text-xs px-3 gap-1" data-testid="tab-cashflow">
                <BarChart3 className="w-3.5 h-3.5 shrink-0" />
                Cash Flow
              </TabsTrigger>
              <TabsTrigger value="reports" className="whitespace-nowrap text-xs px-3 gap-1" data-testid="tab-reports">
                <FileText className="w-3.5 h-3.5 shrink-0" />
                Reports
              </TabsTrigger>
              <TabsTrigger value="tickets" className="whitespace-nowrap text-xs px-3 gap-1" data-testid="tab-tickets">
                <LifeBuoy className="w-3.5 h-3.5 shrink-0" />
                Tickets
              </TabsTrigger>
              <TabsTrigger value="invoices" className="whitespace-nowrap text-xs px-3 gap-1" data-testid="tab-invoices">
                <FileText className="w-3.5 h-3.5 shrink-0" />
                Invoices
              </TabsTrigger>
              <TabsTrigger value="contracts" className="whitespace-nowrap text-xs px-3 gap-1" data-testid="tab-contracts">
                <FileText className="w-3.5 h-3.5 shrink-0" />
                Contracts
              </TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="dashboard" className="mt-4 space-y-4">
            {showLumi && <LumiBriefing apiEndpoint="/api/owner/lumi/briefing" />}

            {stats && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <div className="bg-card border rounded-xl p-3 sm:p-4 text-center">
                  <p className="text-xl sm:text-2xl font-bold" data-testid="text-total-apartments">{stats.totalApartments}</p>
                  <p className="text-[10px] sm:text-xs text-muted-foreground">Properties</p>
                </div>
                <div className="bg-card border rounded-xl p-3 sm:p-4 text-center">
                  <p className="text-xl sm:text-2xl font-bold" data-testid="text-total-submissions">{stats.totalSubmissions}</p>
                  <p className="text-[10px] sm:text-xs text-muted-foreground">Checkouts</p>
                </div>
                <div className="bg-card border rounded-xl p-3 sm:p-4 text-center">
                  <p className="text-xl sm:text-2xl font-bold text-amber-600 dark:text-amber-400" data-testid="text-pending-cleanings">{stats.pendingCleanings}</p>
                  <p className="text-[10px] sm:text-xs text-muted-foreground">Pending</p>
                </div>
                <div className="bg-card border rounded-xl p-3 sm:p-4 text-center">
                  <p className="text-xl sm:text-2xl font-bold text-green-600" data-testid="text-upcoming-checkins">{stats.upcomingCheckIns}</p>
                  <p className="text-[10px] sm:text-xs text-muted-foreground">Check-ins</p>
                </div>
              </div>
            )}

            <div className="grid gap-4 sm:grid-cols-2">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium flex items-center gap-2">
                    <CalendarDays className="w-4 h-4 text-amber-600" />
                    Today's Activity
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {(() => {
                    const todayStr = new Date().toISOString().split("T")[0];
                    const checkIns = typedReservations.filter(r => r.checkInDate && new Date(r.checkInDate).toISOString().split("T")[0] === todayStr && r.status !== "cancelled");
                    const checkOuts = typedReservations.filter(r => r.checkOutDate && new Date(r.checkOutDate).toISOString().split("T")[0] === todayStr && r.status !== "cancelled");
                    if (checkIns.length === 0 && checkOuts.length === 0) {
                      return <p className="text-sm text-muted-foreground py-2" data-testid="text-no-activity">No check-ins or check-outs today</p>;
                    }
                    return (
                      <>
                        {checkIns.map(r => (
                          <div key={`in-${r.id}`} className="flex items-center gap-2 text-sm" data-testid={`activity-checkin-${r.id}`}>
                            <Badge variant="default" className="text-[10px] px-1.5 py-0">IN</Badge>
                            <span className="truncate">{r.guestName}</span>
                            <span className="text-muted-foreground text-xs ml-auto truncate">{getApartmentName(r.apartmentId)}</span>
                          </div>
                        ))}
                        {checkOuts.map(r => (
                          <div key={`out-${r.id}`} className="flex items-center gap-2 text-sm" data-testid={`activity-checkout-${r.id}`}>
                            <Badge variant="outline" className="text-[10px] px-1.5 py-0">OUT</Badge>
                            <span className="truncate">{r.guestName}</span>
                            <span className="text-muted-foreground text-xs ml-auto truncate">{getApartmentName(r.apartmentId)}</span>
                          </div>
                        ))}
                      </>
                    );
                  })()}
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium flex items-center gap-2">
                    <DollarSign className="w-4 h-4 text-green-600" />
                    Financial Summary
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {financialSummary ? (
                    <>
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Booking Revenue</span>
                        <span className="font-medium text-green-600" data-testid="text-dash-revenue">${Number(financialSummary.bookingRevenue || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                      </div>
                      {Number(financialSummary.financialBookingRevenue || 0) === 0 && Number(financialSummary.reservationRevenue || 0) > 0 && (
                        <p className="text-[10px] text-muted-foreground -mt-1">
                          {financialSummary.reservationCount} booking{financialSummary.reservationCount !== 1 ? 's' : ''} · pre-reconciliation
                        </p>
                      )}
                      {Number(financialSummary.serviceCosts || 0) > 0 && (
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">Service Costs</span>
                          <span className="font-medium" data-testid="text-dash-costs">${Number(financialSummary.serviceCosts || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                        </div>
                      )}
                      {Number(financialSummary.pendingPayments || 0) > 0 && (
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">Pending Payments</span>
                          <span className="font-medium text-amber-600" data-testid="text-dash-pending">${Number(financialSummary.pendingPayments || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                        </div>
                      )}
                      {financialSummary.recordCount > 0 && (
                        <>
                          <Separator />
                          <p className="text-[10px] text-muted-foreground font-medium uppercase tracking-wide">Revenue Breakdown</p>
                          <div className="grid grid-cols-2 gap-2 text-xs text-muted-foreground">
                            <div className="flex justify-between">
                              <span>Commissions</span>
                              <span>${Number(financialSummary.totalCommissions || 0).toFixed(2)}</span>
                            </div>
                            <div className="flex justify-between">
                              <span>Check-ins</span>
                              <span>${Number(financialSummary.totalCheckInFees || 0).toFixed(2)}</span>
                            </div>
                          </div>
                          <p className="text-[10px] text-muted-foreground font-medium uppercase tracking-wide mt-1">Cost Breakdown</p>
                          <div className="grid grid-cols-2 gap-2 text-xs text-muted-foreground">
                            <div className="flex justify-between">
                              <span>Cleaning</span>
                              <span>${Number(financialSummary.totalCleaningFees || 0).toFixed(2)}</span>
                            </div>
                            <div className="flex justify-between">
                              <span>Maintenance</span>
                              <span>${Number(financialSummary.totalMaintenance || 0).toFixed(2)}</span>
                            </div>
                          </div>
                        </>
                      )}
                      {Number(financialSummary.bookingRevenue || 0) === 0 && (
                        <p className="text-sm text-muted-foreground py-1" data-testid="text-no-financials">No revenue recorded yet</p>
                      )}
                    </>
                  ) : (
                    <p className="text-sm text-muted-foreground py-2">Loading...</p>
                  )}
                </CardContent>
              </Card>
            </div>

            {typedCleaningJobs.filter(j => j.status === "pending" || j.status === "in_progress").length > 0 && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-amber-600" />
                    Pending Cleaning Jobs
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {typedCleaningJobs.filter(j => j.status === "pending" || j.status === "in_progress").slice(0, 5).map(job => (
                      <div key={job.id} className="flex items-center justify-between gap-2 text-sm" data-testid={`dash-cleaning-${job.id}`}>
                        <span className="truncate">{getApartmentName(job.apartmentId)}</span>
                        <Badge className={`text-[10px] shrink-0 ${job.status === "pending" ? "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400" : "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400"} no-default-hover-elevate no-default-active-elevate`}>
                          {job.status === "pending" ? "Pending" : "In Progress"}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <CalendarDays className="w-4 h-4 text-blue-600" />
                  Upcoming Reservations
                </CardTitle>
              </CardHeader>
              <CardContent>
                {(() => {
                  const now = new Date();
                  now.setHours(0, 0, 0, 0);
                  const upcoming = typedReservations
                    .filter(r => r.status !== "cancelled" && new Date(r.checkInDate) >= now)
                    .sort((a, b) => new Date(a.checkInDate).getTime() - new Date(b.checkInDate).getTime())
                    .slice(0, 5);
                  if (upcoming.length === 0) {
                    return <p className="text-sm text-muted-foreground py-2" data-testid="text-no-upcoming">No upcoming reservations</p>;
                  }
                  return (
                    <div className="space-y-2">
                      {upcoming.map(r => (
                        <div key={r.id} className="flex items-center justify-between gap-2 text-sm" data-testid={`dash-reservation-${r.id}`}>
                          <div className="min-w-0">
                            <p className="font-medium truncate">{r.guestName}</p>
                            <p className="text-xs text-muted-foreground truncate">{getApartmentName(r.apartmentId)}</p>
                          </div>
                          <div className="text-right shrink-0">
                            <p className="text-xs">{formatDate(r.checkInDate)}</p>
                            <p className="text-[10px] text-muted-foreground">{formatDate(r.checkOutDate)}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </CardContent>
            </Card>

            <div className="flex flex-wrap gap-2">
              <Button variant="outline" size="sm" onClick={() => setActiveTab("properties")} data-testid="btn-dash-properties">
                <Home className="w-3.5 h-3.5 mr-1" /> View Properties
              </Button>
              <Button variant="outline" size="sm" onClick={() => setActiveTab("reservations")} data-testid="btn-dash-bookings">
                <CalendarDays className="w-3.5 h-3.5 mr-1" /> Manage Bookings
              </Button>
              <Button variant="outline" size="sm" onClick={() => setActiveTab("finances")} data-testid="btn-dash-finances">
                <DollarSign className="w-3.5 h-3.5 mr-1" /> View Finances
              </Button>
            </div>
          </TabsContent>

          <TabsContent value="properties" className="mt-4 space-y-3">
            <div className="flex justify-end mb-2">
              <Dialog open={createAptOpen} onOpenChange={(open) => { setCreateAptOpen(open); if (!open) setAptForm({ name: "", buildingId: "", bedrooms: "1", maxGuests: "2", checkoutTime: "11:00 AM" }); }}>
                <DialogTrigger asChild>
                  <Button data-testid="button-create-apartment">
                    <Plus className="w-4 h-4 mr-1" /> Add Property
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Add New Property</DialogTitle>
                    <DialogDescription>Create a new apartment/property that will be assigned to you.</DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 pt-2">
                    {(!buildings || (buildings as any[]).length === 0) ? (
                      <div className="text-center py-4 text-muted-foreground" data-testid="text-no-buildings">
                        <p className="text-sm">No buildings are set up yet. Ask your admin to create a building before adding properties.</p>
                      </div>
                    ) : (<>
                    <div>
                      <Label htmlFor="apt-name">Property Name</Label>
                      <Input id="apt-name" placeholder="e.g. Crosby 1412" value={aptForm.name} onChange={(e) => setAptForm(f => ({ ...f, name: e.target.value }))} data-testid="input-apt-name" />
                    </div>
                    <div>
                      <Label htmlFor="apt-building">Building</Label>
                      <Select value={aptForm.buildingId} onValueChange={(v) => setAptForm(f => ({ ...f, buildingId: v }))}>
                        <SelectTrigger data-testid="select-apt-building"><SelectValue placeholder="Select building" /></SelectTrigger>
                        <SelectContent>
                          {(buildings as any[]).map((b: any) => (
                            <SelectItem key={b.id} value={String(b.id)}>{b.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <Label htmlFor="apt-bedrooms">Bedrooms</Label>
                        <Input id="apt-bedrooms" type="number" min="1" value={aptForm.bedrooms} onChange={(e) => setAptForm(f => ({ ...f, bedrooms: e.target.value }))} data-testid="input-apt-bedrooms" />
                      </div>
                      <div>
                        <Label htmlFor="apt-guests">Max Guests</Label>
                        <Input id="apt-guests" type="number" min="1" value={aptForm.maxGuests} onChange={(e) => setAptForm(f => ({ ...f, maxGuests: e.target.value }))} data-testid="input-apt-guests" />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="apt-checkout">Checkout Time</Label>
                      <Input id="apt-checkout" placeholder="11:00 AM" value={aptForm.checkoutTime} onChange={(e) => setAptForm(f => ({ ...f, checkoutTime: e.target.value }))} data-testid="input-apt-checkout" />
                    </div>
                    <Button
                      className="w-full"
                      disabled={!aptForm.name.trim() || !aptForm.buildingId || createAptMutation.isPending}
                      data-testid="button-submit-apartment"
                      onClick={() => {
                        createAptMutation.mutate({
                          name: aptForm.name.trim(),
                          buildingId: Number(aptForm.buildingId),
                          bedrooms: Number(aptForm.bedrooms) || 1,
                          maxGuests: Number(aptForm.maxGuests) || 2,
                          checkoutTime: aptForm.checkoutTime || "11:00",
                        });
                      }}
                    >
                      {createAptMutation.isPending ? "Creating..." : "Create Property"}
                    </Button>
                    </>)}
                  </div>
                </DialogContent>
              </Dialog>
            </div>
            <div className="grid gap-3 md:grid-cols-2">
              {typedApartments.map((apt) => (
                <Card key={apt.id} data-testid={`card-apartment-${apt.id}`}>
                  <CardContent className="p-4 space-y-3">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <h3 className="font-medium" data-testid={`text-apt-name-${apt.id}`}>{apt.name}</h3>
                        <p className="text-sm text-muted-foreground">{apt.slug}</p>
                      </div>
                      <Badge variant="outline">{apt.checkoutTime || "No time set"}</Badge>
                    </div>
                    <div className="flex gap-2 flex-wrap">
                      <Button size="sm" variant="outline" onClick={() => copyGuestLink(apt)} data-testid={`button-copy-link-${apt.id}`}>
                        <Copy className="w-4 h-4 mr-1" /> Copy Guest Link
                      </Button>
                      <Button size="sm" variant="outline" asChild data-testid={`button-preview-${apt.id}`}>
                        <a href={`/g/${apt.slug}?t=${apt.publicToken}`} target="_blank" rel="noopener noreferrer">
                          <ExternalLink className="w-4 h-4 mr-1" /> Preview
                        </a>
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
              {typedApartments.length === 0 && (
                <Card className="md:col-span-2">
                  <CardContent className="p-6 text-center text-muted-foreground">
                    <Home className="w-10 h-10 mx-auto mb-2 opacity-30" />
                    <p>No properties assigned yet. Click "Add Property" to create one.</p>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>

          <TabsContent value="reservations" className="mt-4 space-y-4">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="flex items-center gap-2 flex-wrap">
                <Dialog open={importOpen} onOpenChange={(open) => { setImportOpen(open); if (!open) { setImportResults(null); setImportPreview(null); setImportStep('upload'); setImportFile(null); setImportNameMapping({}); } }}>
                  <DialogTrigger asChild>
                    <Button variant="outline" data-testid="button-csv-import">
                      <Upload className="w-4 h-4 mr-1" />
                      Import CSV
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-lg">
                    <DialogHeader>
                      <DialogTitle>Import Reservations from CSV</DialogTitle>
                      <DialogDescription>
                        Upload a CSV file from Google Calendar, Airbnb, Booking.com, or any spreadsheet.
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                      {importStep === 'upload' && (
                        <>
                          <div className="border-2 border-dashed rounded-lg p-6 text-center space-y-3">
                            <FileSpreadsheet className="w-10 h-10 mx-auto text-muted-foreground" />
                            <div>
                              <input
                                ref={csvFileInputRef}
                                type="file"
                                accept=".csv"
                                className="hidden"
                                data-testid="input-csv-file"
                                onChange={(e) => {
                                  const file = e.target.files?.[0];
                                  if (file) handleCSVPreview(file);
                                }}
                              />
                              <Button
                                variant="outline"
                                onClick={() => csvFileInputRef.current?.click()}
                                disabled={importLoading}
                                data-testid="button-choose-csv"
                              >
                                {importLoading ? "Scanning..." : "Choose CSV File"}
                              </Button>
                            </div>
                            <p className="text-xs text-muted-foreground">
                              Supports columns: Apartment, Guest Name, Guest Email, Guest Phone, Check-In Date, Check-Out Date, Status, Source, Notes, Paid Amount
                            </p>
                          </div>
                          <Separator />
                          <div className="text-xs text-muted-foreground space-y-1">
                            <p className="font-medium">Supported formats:</p>
                            <p>Google Calendar CSV exports are automatically detected. Standard CSV files with columns like Apartment, Guest Name, Check-In Date also work.</p>
                            <p className="font-medium mt-2">Apartment matching:</p>
                            <p>Names are fuzzy-matched to your properties. You can also set aliases for each apartment in its settings.</p>
                          </div>
                        </>
                      )}

                      {importStep === 'mapping' && importPreview && (
                        <>
                          <div className="text-sm space-y-2">
                            <p>Found <strong>{importPreview.totalRows}</strong> rows. <span className="text-green-600">{importPreview.matched} matched</span>, <span className="text-amber-600">{importPreview.unmatched.length} unmatched</span>.</p>
                            <p className="text-muted-foreground text-xs">Map each unmatched name to one of your apartments, or leave unmapped to skip those rows.</p>
                          </div>
                          <div className="max-h-60 overflow-y-auto space-y-3">
                            {importPreview.unmatched.map((name) => (
                              <div key={name} className="border rounded-lg p-3 space-y-1.5">
                                <div className="text-sm font-medium flex items-center gap-2">
                                  <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0" />
                                  <span className="truncate" data-testid={`text-unmatched-${name}`}>"{name}"</span>
                                </div>
                                <Select
                                  value={importNameMapping[name.toLowerCase().trim()]?.toString() || "skip"}
                                  onValueChange={(val) => {
                                    setImportNameMapping(prev => {
                                      const next = { ...prev };
                                      if (val === "skip") {
                                        delete next[name.toLowerCase().trim()];
                                      } else {
                                        next[name.toLowerCase().trim()] = Number(val);
                                      }
                                      return next;
                                    });
                                  }}
                                >
                                  <SelectTrigger className="text-xs" data-testid={`select-mapping-${name}`}>
                                    <SelectValue placeholder="Map to apartment..." />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="skip">Skip (don't import)</SelectItem>
                                    {importPreview.apartments.map(a => (
                                      <SelectItem key={a.id} value={a.id.toString()}>{a.name}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                            ))}
                          </div>
                          <div className="flex items-center gap-2 justify-end">
                            <Button variant="outline" size="sm" onClick={() => { setImportStep('upload'); setImportPreview(null); setImportFile(null); if (csvFileInputRef.current) csvFileInputRef.current.value = ''; }}>
                              Back
                            </Button>
                            <Button
                              size="sm"
                              onClick={handleImportWithMapping}
                              disabled={importLoading}
                              data-testid="button-confirm-import"
                            >
                              {importLoading ? "Importing..." : "Import Now"}
                            </Button>
                          </div>
                          <p className="text-xs text-muted-foreground">
                            Tip: To avoid this step next time, add aliases in each apartment's settings.
                          </p>
                        </>
                      )}

                      {importStep === 'results' && importResults && (
                        <div className="space-y-3">
                          <div className="flex items-center gap-4 text-sm flex-wrap">
                            {importResults.created > 0 && (
                              <span className="flex items-center gap-1 text-green-600">
                                <CheckCircle2 className="w-4 h-4" />
                                {importResults.created} created
                              </span>
                            )}
                            {importResults.updated > 0 && (
                              <span className="flex items-center gap-1 text-blue-600">
                                <RefreshCw className="w-4 h-4" />
                                {importResults.updated} updated
                              </span>
                            )}
                            {importResults.skipped > 0 && (
                              <span className="flex items-center gap-1 text-destructive">
                                <XCircle className="w-4 h-4" />
                                {importResults.skipped} skipped
                              </span>
                            )}
                            <span className="text-muted-foreground">
                              of {importResults.total} rows
                            </span>
                          </div>

                          {importResults.results.filter((r: any) => r.status !== 'created' && r.status !== 'updated').length > 0 && (
                            <div className="max-h-40 overflow-y-auto space-y-1">
                              {importResults.results
                                .filter((r: any) => r.status !== 'created' && r.status !== 'updated')
                                .map((r: any, i: number) => (
                                  <div key={i} className="text-xs flex items-start gap-2 p-2 rounded bg-destructive/10">
                                    <XCircle className="w-3.5 h-3.5 text-destructive shrink-0 mt-0.5" />
                                    <span>
                                      Row {r.row}{r.guestName ? ` (${r.guestName})` : ''}: {r.error}
                                    </span>
                                  </div>
                                ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </DialogContent>
                </Dialog>
                <a href="/api/owner/reservations/ical" download data-testid="link-ical-export">
                  <Button variant="outline" data-testid="button-ical-export">
                    <Calendar className="w-4 h-4 mr-1" />
                    <Download className="w-4 h-4 mr-1" />
                    iCal
                  </Button>
                </a>
              </div>
              <Dialog open={createOpen} onOpenChange={setCreateOpen}>
                <DialogTrigger asChild>
                  <Button onClick={() => setForm({ ...emptyForm })} data-testid="button-create-reservation">
                    <Plus className="w-4 h-4 mr-1" />
                    New Reservation
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Create Reservation</DialogTitle>
                    <DialogDescription>Add a new guest reservation to one of your properties.</DialogDescription>
                  </DialogHeader>
                  <ReservationFormFields />
                  <Button
                    className="w-full"
                    onClick={handleCreate}
                    disabled={createReservation.isPending}
                    data-testid="button-submit-reservation"
                  >
                    {createReservation.isPending ? "Creating..." : "Create Reservation"}
                  </Button>
                </DialogContent>
              </Dialog>
            </div>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2">
                <CardTitle className="text-base">Timeline</CardTitle>
                <div className="flex items-center gap-1">
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => setTimelineOffset((o) => o - 7)}
                    data-testid="button-timeline-prev"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setTimelineOffset(0)}
                    data-testid="button-timeline-today"
                  >
                    Today
                  </Button>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => setTimelineOffset((o) => o + 7)}
                    data-testid="button-timeline-next"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="overflow-x-auto">
                {timelineApartments.length === 0 ? (
                  <p className="text-sm text-muted-foreground py-4 text-center">
                    No reservations to display on the timeline.
                  </p>
                ) : (
                  <div className="min-w-[700px]">
                    <div className="grid" style={{ gridTemplateColumns: "140px repeat(14, 1fr)" }}>
                      <div className="p-2 text-xs font-medium text-muted-foreground border-b">
                        Apartment
                      </div>
                      {timelineDays.map((day, i) => (
                        <div
                          key={i}
                          className={`p-1 text-center text-xs border-b ${
                            isSameDay(day, today) ? "bg-primary/10 font-bold" : ""
                          }`}
                        >
                          <div>{day.toLocaleDateString("en-US", { weekday: "short" })}</div>
                          <div>{day.getDate()}</div>
                        </div>
                      ))}

                      {timelineApartments.map((apt) => (
                        <div key={apt.id} className="contents">
                          <div className="p-2 text-sm font-medium truncate border-b flex items-center">
                            {apt.name}
                          </div>
                          {timelineDays.map((day, di) => {
                            const cellReservations = getReservationsForCell(apt.id, day);
                            return (
                              <div
                                key={`${apt.id}-${di}`}
                                className={`p-0.5 border-b min-h-[36px] ${
                                  isSameDay(day, today) ? "bg-primary/5" : ""
                                }`}
                              >
                                {cellReservations.map((r) => (
                                  <div
                                    key={r.id}
                                    className={`text-[10px] px-1 py-0.5 rounded truncate ${isMyReservation(r) ? getTimelineColor(r.status) : "bg-muted text-muted-foreground border border-dashed"}`}
                                    title={`${r.guestName} (${formatDate(r.checkInDate)} - ${formatDate(r.checkOutDate)})${!isMyReservation(r) ? ` [${(r as any).ownerName || "Other PM"}]` : ""}`}
                                    data-testid={`timeline-block-${r.id}`}
                                  >
                                    {r.guestName}
                                  </div>
                                ))}
                              </div>
                            );
                          })}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {loadingRes ? (
              <div className="text-center py-8 text-muted-foreground">Loading reservations...</div>
            ) : sortedReservations.length === 0 ? (
              <Card>
                <CardContent className="p-6 text-center text-muted-foreground">
                  <CalendarDays className="w-10 h-10 mx-auto mb-2 opacity-30" />
                  <p>No reservations yet. Create one to get started.</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {sortedReservations.map((r) => {
                  const mine = isMyReservation(r);
                  return (
                    <Card key={r.id} className={!mine ? "opacity-60 border-dashed" : ""} data-testid={`card-reservation-${r.id}`}>
                      <CardContent className="p-4 space-y-2">
                        <div className="flex items-start justify-between gap-2 flex-wrap">
                          <div className="min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-medium" data-testid={`text-guest-name-${r.id}`}>
                                {r.guestName}
                              </span>
                              {getStatusBadge(r.status)}
                              {!mine && (
                                <Badge variant="outline" className="text-[10px]" data-testid={`badge-other-pm-${r.id}`}>
                                  {(r as any).ownerName || "Other PM"}
                                </Badge>
                              )}
                            </div>
                            <p className="text-sm text-muted-foreground" data-testid={`text-reservation-apartment-${r.id}`}>
                              {getApartmentName(r.apartmentId)}
                            </p>
                          </div>
                          {mine && (
                            <div className="flex items-center gap-1 shrink-0">
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleEdit(r)}
                                data-testid={`button-edit-reservation-${r.id}`}
                              >
                                <Pencil className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleDelete(r.id)}
                                data-testid={`button-delete-reservation-${r.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          )}
                        </div>
                        <div className="flex items-center gap-4 text-sm text-muted-foreground flex-wrap">
                          <span data-testid={`text-checkin-date-${r.id}`}>
                            <ArrowUpRight className="w-3 h-3 inline mr-1" />
                            {formatDate(r.checkInDate)}
                          </span>
                          <span data-testid={`text-checkout-date-${r.id}`}>
                            <LogOut className="w-3 h-3 inline mr-1" />
                            {formatDate(r.checkOutDate)}
                          </span>
                        </div>
                        {r.notes && (
                          <p className="text-sm text-muted-foreground" data-testid={`text-reservation-notes-${r.id}`}>
                            {r.notes}
                          </p>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}

            <Dialog open={editOpen} onOpenChange={setEditOpen}>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Edit Reservation</DialogTitle>
                  <DialogDescription>Update reservation details.</DialogDescription>
                </DialogHeader>
                <ReservationFormFields />
                <Button
                  className="w-full"
                  onClick={handleUpdate}
                  disabled={updateReservation.isPending}
                  data-testid="button-update-reservation"
                >
                  {updateReservation.isPending ? "Saving..." : "Update Reservation"}
                </Button>
              </DialogContent>
            </Dialog>
          </TabsContent>

          <TabsContent value="cleaning" className="mt-4 space-y-4">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <div className="bg-card border rounded-md p-2.5 sm:p-3 text-center">
                <p className="text-lg sm:text-xl font-bold" data-testid="text-cleaning-count-all">{cleaningCounts.all}</p>
                <p className="text-[10px] sm:text-xs text-muted-foreground">Total Jobs</p>
              </div>
              <div className="bg-card border rounded-md p-2.5 sm:p-3 text-center">
                <p className="text-lg sm:text-xl font-bold text-amber-600 dark:text-amber-400" data-testid="text-cleaning-count-pending">{cleaningCounts.pending}</p>
                <p className="text-[10px] sm:text-xs text-muted-foreground">Pending</p>
              </div>
              <div className="bg-card border rounded-md p-2.5 sm:p-3 text-center">
                <p className="text-lg sm:text-xl font-bold text-blue-600 dark:text-blue-400" data-testid="text-cleaning-count-progress">{cleaningCounts.in_progress}</p>
                <p className="text-[10px] sm:text-xs text-muted-foreground">In Progress</p>
              </div>
              <div className="bg-card border rounded-md p-2.5 sm:p-3 text-center">
                <p className="text-lg sm:text-xl font-bold text-green-600 dark:text-green-400" data-testid="text-cleaning-count-completed">{cleaningCounts.completed}</p>
                <p className="text-[10px] sm:text-xs text-muted-foreground">Completed</p>
              </div>
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              <Select value={cleaningStatusFilter} onValueChange={setCleaningStatusFilter}>
                <SelectTrigger className="w-[140px]" data-testid="select-cleaning-status-filter">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                </SelectContent>
              </Select>

              <Select value={cleaningAptFilter} onValueChange={setCleaningAptFilter}>
                <SelectTrigger className="w-[160px]" data-testid="select-cleaning-apt-filter">
                  <SelectValue placeholder="Apartment" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Properties</SelectItem>
                  {typedApartments.map((a) => (
                    <SelectItem key={a.id} value={String(a.id)}>{a.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={cleaningSortOrder} onValueChange={setCleaningSortOrder}>
                <SelectTrigger className="w-[140px]" data-testid="select-cleaning-sort">
                  <SelectValue placeholder="Sort" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="newest">Newest First</SelectItem>
                  <SelectItem value="oldest">Oldest First</SelectItem>
                  <SelectItem value="scheduled">By Schedule</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {filteredCleaningJobs.length === 0 ? (
              <Card>
                <CardContent className="py-10 px-6 text-center">
                  <Sparkles className="w-12 h-12 mx-auto mb-3 text-muted-foreground/30" />
                  <p className="font-medium text-sm mb-1" data-testid="text-cleaning-empty-title">
                    {typedCleaningJobs.length === 0 ? "No cleaning jobs yet" : "No jobs match your filters"}
                  </p>
                  <p className="text-xs text-muted-foreground" data-testid="text-cleaning-empty-desc">
                    {typedCleaningJobs.length === 0
                      ? "Cleaning jobs will appear automatically when guests check out."
                      : "Try adjusting your status, property, or sort filters to find what you're looking for."}
                  </p>
                  {typedCleaningJobs.length > 0 && cleaningStatusFilter !== "all" && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="mt-3"
                      onClick={() => { setCleaningStatusFilter("all"); setCleaningAptFilter("all"); }}
                      data-testid="button-cleaning-clear-filters"
                    >
                      Clear Filters
                    </Button>
                  )}
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-2">
                {filteredCleaningJobs.map((job) => {
                  const apt = typedApartments.find((a) => a.id === job.apartmentId);
                  const statusCfg = cleaningStatusConfig[job.status] || cleaningStatusConfig.pending;
                  const aptName = job.apartmentName || apt?.name || `Apt #${job.apartmentId}`;
                  const cleanerName = job.assignedToName || null;
                  const cleanerInitials = cleanerName
                    ? cleanerName.split(" ").map((n: string) => n[0]).join("").toUpperCase().slice(0, 2)
                    : null;
                  return (
                    <Card key={job.id} data-testid={`card-cleaning-job-${job.id}`}>
                      <CardContent className="p-3 space-y-2">
                        <div className="flex items-start gap-3">
                          <div className="flex items-center justify-center w-9 h-9 rounded-md bg-muted text-muted-foreground shrink-0 mt-0.5">
                            {cleanerInitials ? (
                              <span className="text-xs font-semibold" data-testid={`avatar-cleaner-${job.id}`}>{cleanerInitials}</span>
                            ) : (
                              <Sparkles className="w-4 h-4" />
                            )}
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-start justify-between gap-2">
                              <div className="min-w-0">
                                <p className="font-semibold text-sm truncate" data-testid={`text-cleaning-apt-${job.id}`}>
                                  {aptName}
                                </p>
                                <div className="flex items-center gap-3 text-xs text-muted-foreground flex-wrap mt-0.5">
                                  {job.scheduledDate && (
                                    <span className="flex items-center gap-1" data-testid={`text-cleaning-date-${job.id}`}>
                                      <Calendar className="w-3 h-3" />
                                      {new Date(job.scheduledDate).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" })}
                                    </span>
                                  )}
                                  {cleanerName && (
                                    <span className="flex items-center gap-1" data-testid={`text-cleaning-cleaner-${job.id}`}>
                                      {cleanerName}
                                      {job.secondCleanerName && ` + ${job.secondCleanerName}`}
                                    </span>
                                  )}
                                  {!job.assignedTo && (
                                    <span className="flex items-center gap-1 text-amber-600 dark:text-amber-400" data-testid={`text-cleaning-unassigned-${job.id}`}>
                                      <AlertTriangle className="w-3 h-3" />
                                      Unassigned
                                    </span>
                                  )}
                                </div>
                              </div>
                              <div className="flex items-center gap-1.5 shrink-0">
                                {(job as any).priority && (job as any).priority !== "normal" && getPriorityBadge((job as any).priority)}
                                <Badge className={statusCfg.className} data-testid={`badge-cleaning-status-${job.id}`}>
                                  {statusCfg.label}
                                </Badge>
                              </div>
                            </div>
                          </div>
                        </div>
                        {job.notes && (
                          <p className="text-xs text-muted-foreground line-clamp-2 ml-12">{job.notes}</p>
                        )}
                        {job.completedAt && (
                          <p className="text-xs text-muted-foreground flex items-center gap-1 ml-12">
                            <CheckCircle2 className="w-3 h-3 text-green-600" />
                            Completed {new Date(job.completedAt).toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" })}
                          </p>
                        )}
                        {job.status === "completed" && <OwnerCleaningPhotoViewer jobId={job.id} />}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>

          <TabsContent value="maintenance" className="mt-4 space-y-3">
            {loadingMaint ? (
              <div className="text-center py-8 text-muted-foreground">Loading...</div>
            ) : (maintenanceJobs as any[]).length === 0 ? (
              <Card>
                <CardContent className="p-8 text-center text-muted-foreground">
                  <Wrench className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>No maintenance jobs for your properties yet.</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {(maintenanceJobs as any[])
                  .sort((a: any, b: any) => {
                    if (a.status === 'pending_approval' && b.status !== 'pending_approval') return -1;
                    if (a.status !== 'pending_approval' && b.status === 'pending_approval') return 1;
                    return 0;
                  })
                  .map((job: any) => {
                  const maintStatusConfig: Record<string, { label: string; variant: "default" | "secondary" | "destructive" | "outline" }> = {
                    pending: { label: "Pending", variant: "destructive" },
                    pending_approval: { label: "Awaiting Your Approval", variant: "destructive" },
                    approved: { label: "Approved", variant: "default" },
                    assigned: { label: "Assigned", variant: "outline" },
                    in_progress: { label: "In Progress", variant: "default" },
                    completed: { label: "Completed", variant: "secondary" },
                    cancelled: { label: "Cancelled", variant: "outline" },
                  };
                  const maintPriorityConfig: Record<string, { label: string; variant: "default" | "secondary" | "destructive" }> = {
                    urgent: { label: "Urgent", variant: "destructive" },
                    high: { label: "High", variant: "destructive" },
                    medium: { label: "Medium", variant: "default" },
                    low: { label: "Low", variant: "secondary" },
                  };
                  const sc = maintStatusConfig[job.status] || maintStatusConfig.pending;
                  const pc = maintPriorityConfig[job.priority || "medium"] || maintPriorityConfig.medium;
                  return (
                    <Card key={job.id} data-testid={`card-owner-maint-${job.id}`}>
                      <CardContent className="p-4 space-y-2">
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0 flex-1">
                            <p className="font-medium text-sm" data-testid={`text-owner-maint-title-${job.id}`}>{job.title}</p>
                            {job.apartmentName && (
                              <p className="text-xs text-muted-foreground flex items-center gap-1">
                                <Home className="w-3 h-3" />{job.apartmentName}
                              </p>
                            )}
                          </div>
                          <div className="flex items-center gap-1 shrink-0">
                            <Badge variant={pc.variant}>{pc.label}</Badge>
                            <Badge variant={sc.variant} data-testid={`badge-owner-maint-status-${job.id}`}>{sc.label}</Badge>
                          </div>
                        </div>
                        {job.description && (
                          <p className="text-sm text-muted-foreground line-clamp-2">{job.description}</p>
                        )}
                        <div className="flex items-center gap-4 text-xs text-muted-foreground flex-wrap">
                          {job.assigneeName && (
                            <span>Assigned: {job.assigneeName}</span>
                          )}
                          {job.scheduledDate && (
                            <span>Scheduled: {new Date(job.scheduledDate + (typeof job.scheduledDate === 'string' && !job.scheduledDate.includes('T') ? 'T12:00:00' : '')).toLocaleDateString("en-US", { timeZone: "UTC" })}</span>
                          )}
                          {job.isBillable && (
                            <span className="flex items-center gap-1">
                              <DollarSign className="w-3 h-3" />
                              ${Number(job.costAmount || 0).toFixed(2)}
                            </span>
                          )}
                        </div>
                        {((job.photos && job.photos.length > 0) || (job.ticketPhotos && job.ticketPhotos.length > 0)) && (() => {
                          const allPhotos: Array<{id: number; url: string; label: string}> = [];
                          if (job.ticketPhotos) {
                            job.ticketPhotos.forEach((url: string, i: number) => {
                              allPhotos.push({ id: -(i + 1), url, label: `Ticket photo ${i + 1}` });
                            });
                          }
                          if (job.photos) {
                            job.photos.forEach((p: any) => {
                              allPhotos.push({
                                id: p.id,
                                url: p.url,
                                label: `${p.phase === 'before' ? 'Before' : 'After'}${p.caption ? ' - ' + p.caption : ''}`,
                              });
                            });
                          }
                          return (
                            <div className="space-y-1 mt-1">
                              {job.ticketPhotos && job.ticketPhotos.length > 0 && (
                                <p className="text-[10px] font-medium text-muted-foreground">Reported issue photos:</p>
                              )}
                              <div className="flex gap-2 flex-wrap">
                                {allPhotos.map((photo, idx) => (
                                  <img
                                    key={photo.id}
                                    src={photo.url}
                                    alt={photo.label}
                                    className="w-16 h-16 object-cover rounded-md border cursor-pointer hover:ring-2 ring-primary transition-all"
                                    onClick={() => openMaintLightbox(allPhotos, idx)}
                                    data-testid={`img-owner-maint-photo-${photo.id}`}
                                  />
                                ))}
                              </div>
                            </div>
                          );
                        })()}
                        {job.completedAt && (
                          <p className="text-xs text-green-600 flex items-center gap-1">
                            <CheckCircle2 className="w-3 h-3" />
                            Completed: {new Date(job.completedAt).toLocaleDateString("en-US", { timeZone: "UTC" })}
                          </p>
                        )}
                        {job.status === 'pending_approval' && (
                          <div className="border-t pt-3 space-y-2">
                            <p className="text-sm font-medium text-amber-700 dark:text-amber-400">
                              This job requires your approval before work begins.
                            </p>
                            {job.costDescription && (
                              <p className="text-xs text-muted-foreground">Cost details: {job.costDescription}</p>
                            )}
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                onClick={async () => {
                                  try {
                                    await apiRequest("POST", `/api/owner/maintenance-jobs/${job.id}/approve`);
                                    queryClientRef.invalidateQueries({ queryKey: ["/api/owner/maintenance-jobs"] });
                                    toast({ title: "Approved", description: "Maintenance job has been approved." });
                                  } catch {
                                    toast({ title: "Error", description: "Failed to approve job", variant: "destructive" });
                                  }
                                }}
                                data-testid={`button-approve-maint-${job.id}`}
                              >
                                <CheckCircle2 className="w-4 h-4 mr-1" />
                                Approve
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={async () => {
                                  const reason = window.prompt("Reason for rejection (optional):");
                                  if (reason === null) return;
                                  try {
                                    await apiRequest("POST", `/api/owner/maintenance-jobs/${job.id}/reject`, { reason });
                                    queryClientRef.invalidateQueries({ queryKey: ["/api/owner/maintenance-jobs"] });
                                    toast({ title: "Rejected", description: "Maintenance job has been rejected." });
                                  } catch {
                                    toast({ title: "Error", description: "Failed to reject job", variant: "destructive" });
                                  }
                                }}
                                data-testid={`button-reject-maint-${job.id}`}
                              >
                                <XCircle className="w-4 h-4 mr-1" />
                                Reject
                              </Button>
                            </div>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}

            <div className="mt-6">
              <h3 className="font-display text-lg font-semibold mb-3 flex items-center gap-2">
                <FileText className="w-5 h-5" /> Repair Estimates & Invoices
              </h3>
              {loadingEstimates ? (
                <div className="text-center py-4 text-muted-foreground text-sm">Loading...</div>
              ) : (repairEstimates as any[]).length === 0 ? (
                <Card>
                  <CardContent className="p-6 text-center text-muted-foreground">
                    <FileText className="w-8 h-8 mx-auto mb-2 opacity-30" />
                    <p className="text-sm">No repair estimates yet.</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-3">
                  {(repairEstimates as any[]).map((est: any) => {
                    const estStatusConfig: Record<string, { label: string; variant: "default" | "secondary" | "destructive" | "outline" }> = {
                      draft: { label: "Draft", variant: "secondary" },
                      sent: { label: "Pending Review", variant: "default" },
                      approved: { label: "Approved", variant: "default" },
                      rejected: { label: "Rejected", variant: "destructive" },
                    };
                    const sc = estStatusConfig[est.status] || estStatusConfig.draft;
                    return (
                      <Card key={est.id} data-testid={`card-owner-estimate-${est.id}`}>
                        <CardContent className="p-4 space-y-2">
                          <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0 flex-1">
                              <p className="font-medium text-sm" data-testid={`text-est-number-${est.id}`}>
                                {est.estimateNumber} — {est.docType === "invoice" ? "Repair Invoice" : "Repair Estimate"}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                {est.apartmentName && <><Home className="w-3 h-3 inline mr-1" />{est.apartmentName} • </>}
                                {est.jobTitle}
                              </p>
                            </div>
                            <div className="flex items-center gap-1 shrink-0">
                              <Badge variant={sc.variant} data-testid={`badge-est-status-${est.id}`}>{sc.label}</Badge>
                            </div>
                          </div>
                          <div className="flex items-center gap-4 text-xs text-muted-foreground flex-wrap">
                            <span className="font-semibold text-foreground text-sm">${Number(est.subtotal).toFixed(2)}</span>
                            <span>{new Date(est.createdAt).toLocaleDateString()}</span>
                            {est.sentAt && <span>Sent: {new Date(est.sentAt).toLocaleDateString()}</span>}
                          </div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={async () => {
                                const r = await fetch(`/api/my/maintenance-estimates/${est.id}/pdf`, { credentials: "include" });
                                if (!r.ok) { toast({ title: "Error", description: "Failed to download PDF", variant: "destructive" }); return; }
                                const blob = await r.blob();
                                const url = URL.createObjectURL(blob);
                                const a = document.createElement("a"); a.href = url; a.download = `${est.estimateNumber}.pdf`; a.click();
                                URL.revokeObjectURL(url);
                              }}
                              data-testid={`button-download-est-${est.id}`}
                            >
                              <Download className="w-3 h-3 mr-1" />Download PDF
                            </Button>
                            {est.status === "sent" && (
                              <>
                                <Button
                                  size="sm"
                                  onClick={async () => {
                                    try {
                                      await apiRequest("PATCH", `/api/my/maintenance-estimates/${est.id}`, { status: "approved" });
                                      queryClientRef.invalidateQueries({ queryKey: ["/api/my/maintenance-estimates"] });
                                      toast({ title: "Approved", description: "Repair estimate has been approved." });
                                    } catch {
                                      toast({ title: "Error", description: "Failed to approve estimate", variant: "destructive" });
                                    }
                                  }}
                                  data-testid={`button-approve-est-${est.id}`}
                                >
                                  <CheckCircle2 className="w-4 h-4 mr-1" />Approve
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={async () => {
                                    try {
                                      await apiRequest("PATCH", `/api/my/maintenance-estimates/${est.id}`, { status: "rejected" });
                                      queryClientRef.invalidateQueries({ queryKey: ["/api/my/maintenance-estimates"] });
                                      toast({ title: "Rejected", description: "Repair estimate has been rejected." });
                                    } catch {
                                      toast({ title: "Error", description: "Failed to reject estimate", variant: "destructive" });
                                    }
                                  }}
                                  data-testid={`button-reject-est-${est.id}`}
                                >
                                  <XCircle className="w-4 h-4 mr-1" />Reject
                                </Button>
                              </>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="finances" className="mt-4 space-y-4">
            {financialSummary && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <div className="bg-card border rounded-xl p-3 text-center">
                  <p className="text-lg sm:text-2xl font-bold" data-testid="text-total-checkin-fees">
                    ${Number(financialSummary.totalCheckInFees || 0).toFixed(2)}
                  </p>
                  <p className="text-[10px] sm:text-xs text-muted-foreground">Check-in Fees</p>
                </div>
                <div className="bg-card border rounded-xl p-3 text-center">
                  <p className="text-lg sm:text-2xl font-bold" data-testid="text-total-cleaning-fees">
                    ${Number(financialSummary.totalCleaningFees || 0).toFixed(2)}
                  </p>
                  <p className="text-[10px] sm:text-xs text-muted-foreground">Cleaning Fees</p>
                </div>
                <div className="bg-card border rounded-xl p-3 text-center">
                  <p className="text-lg sm:text-2xl font-bold" data-testid="text-total-owed">
                    ${Number(financialSummary.totalOwed || 0).toFixed(2)}
                  </p>
                  <p className="text-[10px] sm:text-xs text-muted-foreground">Total Owed</p>
                </div>
                <div className="bg-card border rounded-xl p-3 text-center">
                  <p className="text-lg sm:text-2xl font-bold" data-testid="text-pending-payments">
                    {financialSummary.pendingPayments || 0}
                  </p>
                  <p className="text-[10px] sm:text-xs text-muted-foreground">Pending</p>
                </div>
              </div>
            )}

            {loadingFinances ? (
              <div className="text-center py-8 text-muted-foreground">Loading financial records...</div>
            ) : (financialRecords as any[]).length === 0 ? (
              <Card>
                <CardContent className="p-6 text-center text-muted-foreground">
                  <DollarSign className="w-10 h-10 mx-auto mb-2 opacity-30" />
                  <p>No financial records yet</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-2">
                {(financialRecords as any[]).map((record: any) => {
                  const apt = (allApartments as Apartment[]).find((a) => a.id === record.apartmentId);
                  return (
                    <Card key={record.id} data-testid={`card-financial-record-${record.id}`}>
                      <CardContent className="p-3 flex items-center justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            {record.type === "check_in" ? (
                              <Badge className="bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400 no-default-hover-elevate no-default-active-elevate" data-testid={`badge-type-${record.id}`}>
                                Check-in
                              </Badge>
                            ) : record.type === "unit_setup" ? (
                              <Badge className="bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400 no-default-hover-elevate no-default-active-elevate" data-testid={`badge-type-${record.id}`}>
                                Unit Setup
                              </Badge>
                            ) : record.type === "maintenance" ? (
                              <Badge className="bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400 no-default-hover-elevate no-default-active-elevate" data-testid={`badge-type-${record.id}`}>
                                Maintenance
                              </Badge>
                            ) : record.type === "miscellaneous" ? (
                              <Badge className="bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400 no-default-hover-elevate no-default-active-elevate" data-testid={`badge-type-${record.id}`}>
                                Miscellaneous
                              </Badge>
                            ) : (
                              <Badge className="bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400 no-default-hover-elevate no-default-active-elevate" data-testid={`badge-type-${record.id}`}>
                                Cleaning
                              </Badge>
                            )}
                            <span className="font-medium text-sm" data-testid={`text-amount-${record.id}`}>
                              ${Number(record.totalAmount || 0).toFixed(2)}
                            </span>
                          </div>
                          <p className="text-xs text-muted-foreground mt-1 truncate" data-testid={`text-description-${record.id}`}>
                            {record.description || (apt?.name ? `${apt.name}` : `Apartment #${record.apartmentId}`)}
                          </p>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          {record.status === "paid" ? (
                            <Badge className="bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400 no-default-hover-elevate no-default-active-elevate" data-testid={`badge-status-${record.id}`}>
                              Paid
                            </Badge>
                          ) : record.status === "cancelled" ? (
                            <Badge variant="destructive" data-testid={`badge-status-${record.id}`}>
                              Cancelled
                            </Badge>
                          ) : (
                            <Badge data-testid={`badge-status-${record.id}`}>
                              Pending
                            </Badge>
                          )}
                          <span className="text-xs text-muted-foreground" data-testid={`text-date-${record.id}`}>
                            {record.createdAt ? formatDate(record.createdAt) : ""}
                          </span>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}

            <Separator className="my-4" />
            <h3 className="font-semibold text-lg flex items-center gap-2">
              <Shield className="w-5 h-5" />
              Security Deposits
            </h3>
            {loadingDeposits ? (
              <div className="text-center py-4 text-muted-foreground">Loading deposits...</div>
            ) : (ownerDeposits as any[]).length === 0 ? (
              <Card>
                <CardContent className="p-6 text-center text-muted-foreground">
                  <Shield className="w-10 h-10 mx-auto mb-2 opacity-30" />
                  <p>No security deposits recorded</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-2">
                {(ownerDeposits as any[]).map((deposit: any) => {
                  const apt = (allApartments as Apartment[]).find((a) => a.id === deposit.apartmentId);
                  return (
                    <Card key={deposit.id} data-testid={`card-deposit-${deposit.id}`}>
                      <CardContent className="p-3 flex items-center justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium text-sm" data-testid={`text-deposit-guest-${deposit.id}`}>
                              {deposit.guestName || "Guest"}
                            </span>
                            <Badge className={
                              deposit.status === "collected" ? "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400 no-default-hover-elevate no-default-active-elevate" :
                              deposit.status === "held" ? "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400 no-default-hover-elevate no-default-active-elevate" :
                              deposit.status === "returned" ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400 no-default-hover-elevate no-default-active-elevate" :
                              deposit.status === "partially_returned" ? "bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400 no-default-hover-elevate no-default-active-elevate" :
                              "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400 no-default-hover-elevate no-default-active-elevate"
                            } data-testid={`badge-deposit-status-${deposit.id}`}>
                              {deposit.status.replace(/_/g, " ")}
                            </Badge>
                          </div>
                          <p className="text-xs text-muted-foreground mt-1">
                            {apt?.name || `Apt #${deposit.apartmentId}`}
                            {deposit.paymentMethod && ` - ${deposit.paymentMethod.replace(/_/g, " ")}`}
                          </p>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <span className="font-semibold" data-testid={`text-deposit-amount-${deposit.id}`}>
                            ${Number(deposit.amount || 0).toFixed(2)}
                          </span>
                          {deposit.returnedAmount && Number(deposit.returnedAmount) > 0 && (
                            <span className="text-xs text-muted-foreground">
                              (Returned: ${Number(deposit.returnedAmount).toFixed(2)})
                            </span>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>

          <TabsContent value="cashflow" className="mt-4 space-y-4">
            <OwnerCashFlowTab />
          </TabsContent>

          <TabsContent value="reports" className="mt-4 space-y-4">
            <Card>
              <CardContent className="pt-4 pb-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="text-sm font-medium text-muted-foreground mb-1.5 block">Period</label>
                    <Select value={reportPreset} onValueChange={handleReportPreset}>
                      <SelectTrigger data-testid="select-report-preset" className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="this_week">This Week</SelectItem>
                        <SelectItem value="last_week">Last Week</SelectItem>
                        <SelectItem value="this_month">This Month</SelectItem>
                        <SelectItem value="last_month">Last Month</SelectItem>
                        <SelectItem value="this_year">This Year</SelectItem>
                        <SelectItem value="custom">Custom Range</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-muted-foreground mb-1.5 block">Property</label>
                    <Select value={reportAptFilter} onValueChange={setReportAptFilter}>
                      <SelectTrigger data-testid="select-report-apartment" className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Properties</SelectItem>
                        {typedApartments.map((a) => (
                          <SelectItem key={a.id} value={String(a.id)}>{a.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {reportPreset === "custom" && (
                    <div className="sm:col-span-2">
                      <label className="text-sm font-medium text-muted-foreground mb-1.5 block">Date Range</label>
                      <Popover open={reportCalendarOpen} onOpenChange={setReportCalendarOpen}>
                        <PopoverTrigger asChild>
                          <Button variant="outline" className="w-full justify-start text-left font-normal" data-testid="button-report-date-picker">
                            <CalendarDays className="mr-2 h-4 w-4" />
                            {reportDateRange?.from ? (
                              reportDateRange.to ? (
                                <span>{formatDateShort(reportDateRange.from)} - {formatDateShort(reportDateRange.to)}</span>
                              ) : (
                                <span>{formatDateShort(reportDateRange.from)}</span>
                              )
                            ) : (
                              <span className="text-muted-foreground">Pick dates</span>
                            )}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <CalendarWidget
                            mode="range"
                            selected={reportDateRange}
                            onSelect={(range) => {
                              setReportDateRange(range);
                              if (range?.from && range?.to) {
                                setReportCalendarOpen(false);
                              }
                            }}
                            numberOfMonths={1}
                          />
                        </PopoverContent>
                      </Popover>
                    </div>
                  )}
                </div>

                {reportDateRange?.from && reportDateRange?.to && (
                  <div className="mt-3 flex items-center justify-between gap-2 flex-wrap">
                    <p className="text-sm text-muted-foreground">
                      {formatDate(reportDateRange.from)} - {formatDate(reportDateRange.to)}
                    </p>
                    <Button variant="outline" size="sm" onClick={() => window.print()} data-testid="button-print-owner-report">
                      <Printer className="w-4 h-4 mr-1" />
                      Print
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {loadingReport ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
              </div>
            ) : reportData ? (
              <div className="space-y-4 print-report">
                <div className="print-header hidden print:block mb-4">
                  <h1 className="text-xl font-bold">Property Report</h1>
                  <p className="text-sm text-muted-foreground">
                    {reportDateRange?.from && reportDateRange?.to
                      ? `${formatDate(reportDateRange.from)} - ${formatDate(reportDateRange.to)}`
                      : ""}
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <Card>
                    <CardContent className="pt-4 pb-3">
                      <div className="text-lg font-bold" data-testid="stat-report-reservations">{reportData?.reservations?.summary?.total || 0}</div>
                      <p className="text-xs text-muted-foreground">Reservations</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-4 pb-3">
                      <div className="text-lg font-bold" data-testid="stat-report-cleaning">{reportData?.cleaning?.summary?.total || 0}</div>
                      <p className="text-xs text-muted-foreground">Cleaning Jobs</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-4 pb-3">
                      <div className="text-lg font-bold" data-testid="stat-report-checkouts">{reportData?.submissions?.summary?.total || 0}</div>
                      <p className="text-xs text-muted-foreground">Guest Checkouts</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-4 pb-3">
                      <div className="text-lg font-bold" data-testid="stat-report-charges">{formatMoney(reportData?.finances?.summary?.totalCharges)}</div>
                      <p className="text-xs text-muted-foreground">Total Charges</p>
                    </CardContent>
                  </Card>
                </div>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Cost Breakdown</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Check-in Fees</span>
                      <span className="font-medium">{formatMoney(reportData?.finances?.summary?.checkInFees)}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Cleaning Fees</span>
                      <span className="font-medium">{formatMoney(reportData?.finances?.summary?.cleaningFees)}</span>
                    </div>
                    {Number(reportData?.finances?.summary?.unitSetupFees || 0) > 0 && (
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-muted-foreground">Unit Setup Fees</span>
                        <span className="font-medium">{formatMoney(reportData?.finances?.summary?.unitSetupFees)}</span>
                      </div>
                    )}
                    {Number(reportData?.finances?.summary?.maintenanceFees || 0) > 0 && (
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-muted-foreground">Maintenance</span>
                        <span className="font-medium">{formatMoney(reportData?.finances?.summary?.maintenanceFees)}</span>
                      </div>
                    )}
                    {Number(reportData?.finances?.summary?.miscFees || 0) > 0 && (
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-muted-foreground">Miscellaneous</span>
                        <span className="font-medium">{formatMoney(reportData?.finances?.summary?.miscFees)}</span>
                      </div>
                    )}
                    <Separator />
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">Total Charges</span>
                      <span className="font-bold">{formatMoney(reportData?.finances?.summary?.totalCharges)}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Pending Payment</span>
                      <span className="font-medium text-orange-600 dark:text-orange-400">{formatMoney(reportData?.finances?.summary?.pendingAmount)}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Paid</span>
                      <span className="font-medium text-green-600 dark:text-green-400">{formatMoney(reportData?.finances?.summary?.paidAmount)}</span>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Reservations ({reportData?.reservations?.summary?.total || 0})</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto -mx-6">
                      <div className="min-w-[400px] px-6">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Guest</TableHead>
                              <TableHead>Property</TableHead>
                              <TableHead>Check-in</TableHead>
                              <TableHead>Check-out</TableHead>
                              <TableHead>Status</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {(reportData?.reservations?.records || []).length === 0 ? (
                              <TableRow>
                                <TableCell colSpan={5} className="text-center text-muted-foreground py-6">
                                  No reservations in this period
                                </TableCell>
                              </TableRow>
                            ) : (
                              (reportData?.reservations?.records || []).map((r: any) => (
                                <TableRow key={r.id} data-testid={`row-report-res-${r.id}`}>
                                  <TableCell className="font-medium">{r.guestName}</TableCell>
                                  <TableCell>{r.apartmentName}</TableCell>
                                  <TableCell>{formatDateShort(r.checkInDate)}</TableCell>
                                  <TableCell>{formatDateShort(r.checkOutDate)}</TableCell>
                                  <TableCell>{getStatusBadge(r.status)}</TableCell>
                                </TableRow>
                              ))
                            )}
                          </TableBody>
                        </Table>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Cleaning ({reportData?.cleaning?.summary?.total || 0})</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto -mx-6">
                      <div className="min-w-[400px] px-6">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Property</TableHead>
                              <TableHead>Status</TableHead>
                              <TableHead>Created</TableHead>
                              <TableHead>Completed</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {(reportData?.cleaning?.records || []).length === 0 ? (
                              <TableRow>
                                <TableCell colSpan={4} className="text-center text-muted-foreground py-6">
                                  No cleaning jobs in this period
                                </TableCell>
                              </TableRow>
                            ) : (
                              (reportData?.cleaning?.records || []).map((j: any) => (
                                <TableRow key={j.id} data-testid={`row-report-clean-${j.id}`}>
                                  <TableCell className="font-medium">{j.apartmentName}</TableCell>
                                  <TableCell>
                                    {j.status === "completed" ? (
                                      <Badge className="bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400 no-default-hover-elevate no-default-active-elevate">Completed</Badge>
                                    ) : j.status === "in_progress" ? (
                                      <Badge variant="secondary">In Progress</Badge>
                                    ) : (
                                      <Badge variant="outline">{j.status}</Badge>
                                    )}
                                  </TableCell>
                                  <TableCell>{j.createdAt ? formatDateShort(j.createdAt) : "-"}</TableCell>
                                  <TableCell>{j.completedAt ? formatDateShort(j.completedAt) : "-"}</TableCell>
                                </TableRow>
                              ))
                            )}
                          </TableBody>
                        </Table>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Charges ({(reportData?.finances?.records || []).length})</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto -mx-6">
                      <div className="min-w-[350px] px-6">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Type</TableHead>
                              <TableHead>Property</TableHead>
                              <TableHead className="text-right">Amount</TableHead>
                              <TableHead>Status</TableHead>
                              <TableHead>Date</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {(reportData?.finances?.records || []).length === 0 ? (
                              <TableRow>
                                <TableCell colSpan={5} className="text-center text-muted-foreground py-6">
                                  No charges in this period
                                </TableCell>
                              </TableRow>
                            ) : (
                              (reportData?.finances?.records || []).map((f: any) => (
                                <TableRow key={f.id} data-testid={`row-report-fin-${f.id}`}>
                                  <TableCell>
                                    <Badge variant="outline" className="text-xs">{f.type === "check_in" ? "Check-in" : f.type === "unit_setup" ? "Unit Setup" : f.type === "maintenance" ? "Maintenance" : f.type === "miscellaneous" ? "Misc" : "Cleaning"}</Badge>
                                  </TableCell>
                                  <TableCell className="font-medium">{f.apartmentName}</TableCell>
                                  <TableCell className="text-right font-medium">{formatMoney(f.totalAmount)}</TableCell>
                                  <TableCell>
                                    {f.status === "paid" ? (
                                      <Badge className="bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400 no-default-hover-elevate no-default-active-elevate text-xs">Paid</Badge>
                                    ) : f.status === "cancelled" ? (
                                      <Badge variant="destructive" className="text-xs">Cancelled</Badge>
                                    ) : (
                                      <Badge className="text-xs">Pending</Badge>
                                    )}
                                  </TableCell>
                                  <TableCell>{f.createdAt ? formatDateShort(f.createdAt) : "-"}</TableCell>
                                </TableRow>
                              ))
                            )}
                          </TableBody>
                        </Table>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            ) : (
              <Card>
                <CardContent className="py-8 text-center text-muted-foreground">
                  Select a date range to generate your report
                </CardContent>
              </Card>
            )}

          </TabsContent>

          <TabsContent value="tickets" className="mt-4 space-y-4">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <h2 className="text-lg font-semibold" data-testid="text-tickets-heading">Support Tickets</h2>
              <Dialog open={ticketCreateOpen} onOpenChange={setTicketCreateOpen}>
                <DialogTrigger asChild>
                  <Button data-testid="button-create-ticket">
                    <Plus className="w-4 h-4 mr-1" />
                    New Ticket
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Create Support Ticket</DialogTitle>
                    <DialogDescription>Submit a new support request.</DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div>
                      <Label>Subject</Label>
                      <Input
                        value={ticketForm.subject}
                        onChange={(e) => setTicketForm((p) => ({ ...p, subject: e.target.value }))}
                        placeholder="Brief description of your issue"
                        data-testid="input-ticket-subject"
                      />
                    </div>
                    <div>
                      <Label>Category</Label>
                      <Select value={ticketForm.category} onValueChange={(v) => setTicketForm((p) => ({ ...p, category: v, apartmentId: v !== "maintenance" ? "none" : p.apartmentId }))}>
                        <SelectTrigger data-testid="select-ticket-category">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="general">General</SelectItem>
                          <SelectItem value="access">Access</SelectItem>
                          <SelectItem value="maintenance">Maintenance</SelectItem>
                          <SelectItem value="cleaning_issue">Cleaning Issue</SelectItem>
                          <SelectItem value="billing">Billing</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {ticketForm.category === "maintenance" && (
                      <div>
                        <Label>Unit (optional)</Label>
                        <Select value={ticketForm.apartmentId} onValueChange={(v) => setTicketForm((p) => ({ ...p, apartmentId: v }))}>
                          <SelectTrigger data-testid="select-ticket-apartment">
                            <SelectValue placeholder="Select a unit..." />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">None</SelectItem>
                            {(apartments as Apartment[]).map((apt) => (
                              <SelectItem key={apt.id} value={String(apt.id)}>{apt.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    <div>
                      <Label>Details</Label>
                      <Textarea
                        value={ticketForm.body}
                        onChange={(e) => setTicketForm((p) => ({ ...p, body: e.target.value }))}
                        placeholder="Describe your issue in detail..."
                        className="resize-none"
                        data-testid="input-ticket-body"
                      />
                    </div>
                    <Button
                      className="w-full"
                      onClick={() => {
                        const payload: any = { subject: ticketForm.subject, category: ticketForm.category, body: ticketForm.body };
                        if (ticketForm.apartmentId && ticketForm.apartmentId !== "none") payload.apartmentId = Number(ticketForm.apartmentId);
                        createTicketMutation.mutate(payload);
                      }}
                      disabled={!ticketForm.subject.trim() || createTicketMutation.isPending}
                      data-testid="button-submit-ticket"
                    >
                      {createTicketMutation.isPending ? "Submitting..." : "Submit Ticket"}
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>

            {loadingTickets ? (
              <div className="space-y-3">
                {[1, 2, 3].map((i) => (
                  <Skeleton key={i} className="h-20 w-full rounded-md" />
                ))}
              </div>
            ) : ownerTickets.length === 0 ? (
              <Card>
                <CardContent className="p-8 text-center text-muted-foreground" data-testid="text-no-tickets">
                  <LifeBuoy className="w-10 h-10 mx-auto mb-3 opacity-40" />
                  <p>No support tickets yet.</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {ownerTickets.map((ticket: any) => {
                  const sc = ticketStatusConfig[ticket.status] || ticketStatusConfig.open;
                  const pc = ticketPriorityConfig[ticket.priority] || ticketPriorityConfig.medium;
                  return (
                    <Card
                      key={ticket.id}
                      className="hover-elevate cursor-pointer"
                      onClick={() => { setSelectedTicket(ticket); setTicketDetailOpen(true); setTicketReplyText(""); }}
                      data-testid={`card-ticket-${ticket.id}`}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between gap-3 flex-wrap">
                          <div className="min-w-0 flex-1 space-y-1">
                            <p className="font-medium truncate" data-testid={`text-ticket-subject-${ticket.id}`}>
                              {ticket.subject}
                            </p>
                            <div className="flex items-center gap-2 flex-wrap text-xs text-muted-foreground">
                              <span data-testid={`text-ticket-category-${ticket.id}`}>
                                {ticketCategoryLabels[ticket.category] || ticket.category}
                              </span>
                              <span>
                                <Clock className="w-3 h-3 inline mr-0.5" />
                                {new Date(ticket.createdAt).toLocaleDateString("en-US", { timeZone: "UTC" })}
                              </span>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            <Badge variant={pc.variant} className="no-default-active-elevate" data-testid={`badge-ticket-priority-${ticket.id}`}>
                              {pc.label}
                            </Badge>
                            <Badge variant={sc.variant} className="no-default-active-elevate" data-testid={`badge-ticket-status-${ticket.id}`}>
                              {sc.label}
                            </Badge>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}

            <Dialog open={ticketDetailOpen} onOpenChange={(open) => { setTicketDetailOpen(open); if (!open) setSelectedTicket(null); }}>
              <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col">
                <DialogHeader>
                  <DialogTitle data-testid="text-ticket-detail-subject">{selectedTicket?.subject}</DialogTitle>
                  <DialogDescription>
                    {ticketCategoryLabels[selectedTicket?.category] || selectedTicket?.category} &middot; Created {selectedTicket?.createdAt ? new Date(selectedTicket.createdAt).toLocaleString() : ""}
                  </DialogDescription>
                </DialogHeader>

                {selectedTicket && (
                  <div className="space-y-4 flex-1 overflow-y-auto">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge variant={(ticketStatusConfig[selectedTicket.status] || ticketStatusConfig.open).variant} data-testid="badge-detail-status">
                        {(ticketStatusConfig[selectedTicket.status] || ticketStatusConfig.open).label}
                      </Badge>
                      <Badge variant={(ticketPriorityConfig[selectedTicket.priority] || ticketPriorityConfig.medium).variant} data-testid="badge-detail-priority">
                        {(ticketPriorityConfig[selectedTicket.priority] || ticketPriorityConfig.medium).label}
                      </Badge>
                    </div>

                    <div className="space-y-3">
                      <div className="flex items-center gap-2">
                        <MessageSquare className="w-4 h-4 text-muted-foreground" />
                        <Label className="text-sm font-semibold">Messages</Label>
                      </div>
                      {messagesLoading ? (
                        <div className="space-y-2">
                          <Skeleton className="h-16 w-full" />
                          <Skeleton className="h-16 w-full" />
                        </div>
                      ) : ticketMessages.length === 0 ? (
                        <p className="text-sm text-muted-foreground" data-testid="text-no-messages">No messages yet.</p>
                      ) : (
                        <div className="space-y-3 max-h-[40vh] overflow-y-auto">
                          {ticketMessages.map((msg: any) => (
                            <Card key={msg.id} data-testid={`card-message-${msg.id}`}>
                              <CardContent className="p-3 space-y-1">
                                <div className="flex items-center justify-between gap-2 flex-wrap">
                                  <span className="text-sm font-medium" data-testid={`text-message-sender-${msg.id}`}>
                                    {msg.senderName || `User #${msg.senderUserId}` || "System"}
                                  </span>
                                  <span className="text-xs text-muted-foreground">
                                    {new Date(msg.createdAt).toLocaleString()}
                                  </span>
                                </div>
                                <p className="text-sm" data-testid={`text-message-body-${msg.id}`}>{msg.body}</p>
                              </CardContent>
                            </Card>
                          ))}
                        </div>
                      )}
                    </div>

                    <div className="space-y-2 pt-2 border-t">
                      <Label className="text-sm">Reply</Label>
                      <Textarea
                        value={ticketReplyText}
                        onChange={(e) => setTicketReplyText(e.target.value)}
                        placeholder="Type your reply..."
                        className="resize-none"
                        data-testid="input-ticket-reply"
                      />
                      <Button
                        onClick={() => { if (ticketReplyText.trim() && selectedTicket) ticketReplyMutation.mutate({ id: selectedTicket.id, body: ticketReplyText.trim() }); }}
                        disabled={!ticketReplyText.trim() || ticketReplyMutation.isPending}
                        data-testid="button-send-ticket-reply"
                      >
                        <Send className="w-4 h-4 mr-2" />
                        {ticketReplyMutation.isPending ? "Sending..." : "Send Reply"}
                      </Button>
                    </div>
                  </div>
                )}
              </DialogContent>
            </Dialog>

            <style>{`
              @page {
                margin: 0.5in;
                margin-top: 0.3in;
                margin-bottom: 0.3in;
              }
              @media print {
                .no-print, nav, header, [data-testid="tabs-navigation"] { display: none !important; }
                .print-report { padding: 0 !important; }
                .print-report .hidden.print\\:block { display: block !important; }
                body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
              }
            `}</style>
          </TabsContent>

          <TabsContent value="invoices" className="mt-4 space-y-4">
            <OwnerInvoicesTab />
          </TabsContent>

          <TabsContent value="contracts" className="mt-4 space-y-4">
            <ContractsWidget />
          </TabsContent>

        </Tabs>

        <AlertDialog open={!!overlapConflict} onOpenChange={(open) => { if (!open) setOverlapConflict(null); }}>
          <AlertDialogContent data-testid="dialog-overlap-conflict">
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-destructive" />
                Reservation Conflict
              </AlertDialogTitle>
              <AlertDialogDescription asChild>
                <div>
                  <p className="mb-3">
                    This reservation overlaps with existing bookings:
                  </p>
                  <div className="space-y-2">
                    {overlapConflict?.overlaps.map((o: any, i: number) => (
                      <div key={i} className="rounded-md border p-3 text-sm" data-testid={`overlap-item-${i}`}>
                        <p className="font-medium">{o.guestName}</p>
                        <p className="text-muted-foreground">
                          {formatDate(o.checkInDate)} — {formatDate(o.checkOutDate)}
                        </p>
                      </div>
                    ))}
                  </div>
                  <p className="mt-3 text-sm text-destructive" data-testid="text-conflict-no-override">
                    Cannot create reservation — a scheduling conflict exists. Please choose different dates or contact an administrator.
                  </p>
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="button-conflict-cancel">Close</AlertDialogCancel>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
      {maintLightbox && (
        <PhotoLightbox photos={maintLightbox.photos} initialIndex={maintLightbox.index} onClose={closeMaintLightbox} />
      )}
      {showLumi && <LumiOwnerBubble />}
    </div>
  );
}

const OWNER_PHOTO_CATEGORIES = [
  { key: 'kitchen', label: 'Kitchen', icon: '🍳' },
  { key: 'living_room', label: 'Living Room', icon: '🛋️' },
  { key: 'bedrooms', label: 'Bedrooms', icon: '🛏️' },
  { key: 'bathrooms', label: 'Bathrooms', icon: '🚿' },
  { key: 'floor', label: 'Floor', icon: '🧹' },
  { key: 'fridge', label: 'Fridge', icon: '❄️' },
  { key: 'all', label: 'Other Areas', icon: '🏠' },
];

function OwnerCleaningPhotoViewer({ jobId }: { jobId: number }) {
  const { data: photos = [] } = useQuery<any[]>({
    queryKey: ['/api/cleaning-jobs', jobId, 'photos'],
    staleTime: 0, // always re-fetch — photos uploaded after first view would never appear otherwise
    refetchOnMount: true,
  });
  const { lightboxState, openLightbox, closeLightbox } = useLightbox();

  if (!photos || photos.length === 0) return null;

  const photosByCategory = OWNER_PHOTO_CATEGORIES.reduce((acc, cat) => {
    acc[cat.key] = (photos as any[]).filter(p => p.category === cat.key);
    return acc;
  }, {} as Record<string, any[]>);

  const hasPhotos = OWNER_PHOTO_CATEGORIES.some(cat => photosByCategory[cat.key]?.length > 0);
  if (!hasPhotos) return null;

  const allPhotos = OWNER_PHOTO_CATEGORIES.flatMap(cat =>
    photosByCategory[cat.key]?.map((p: any) => ({ id: p.id, url: p.url, label: `${cat.icon} ${cat.label}` })) || []
  );
  const getGlobalIndex = (photo: any) => allPhotos.findIndex(p => p.id === photo.id);

  return (
    <div className="space-y-2 pt-2 border-t" data-testid={`photos-owner-${jobId}`}>
      <Label className="text-xs text-muted-foreground flex items-center gap-1">
        <Camera className="w-3 h-3" /> Cleaning Photos ({photos.length})
      </Label>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {OWNER_PHOTO_CATEGORIES.filter(cat => photosByCategory[cat.key]?.length > 0).map(cat => (
          <div key={cat.key} className="space-y-1" data-testid={`photos-owner-${cat.key}-${jobId}`}>
            <span className="text-[10px] font-medium text-muted-foreground">{cat.icon} {cat.label}</span>
            <div className="flex gap-1.5 flex-wrap">
              {photosByCategory[cat.key].map((photo: any) => (
                <img
                  key={photo.id}
                  src={photo.url}
                  alt={cat.label}
                  className="w-16 h-16 rounded object-cover hover:ring-2 ring-primary transition-all cursor-pointer"
                  onClick={() => openLightbox(allPhotos, getGlobalIndex(photo))}
                  data-testid={`img-owner-${cat.key}-${photo.id}`}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
      {lightboxState && (
        <PhotoLightbox photos={lightboxState.photos} initialIndex={lightboxState.index} onClose={closeLightbox} />
      )}
    </div>
  );
}
